import React, {useCallback, useEffect, useMemo, useState} from 'react';
import {
    Card,
    CardContent,
    CircularProgress,
    Divider,
    Typography,
    IconButton,
} from '@material-ui/core';
import {
    ArrowBack,
    ArrowDropDown,
    ArrowDropUp,
    ArrowLeft,
    ArrowRight,
    ChevronLeft,
    ChevronRight,
    Close,
    ExpandLessOutlined,
    ExpandMoreOutlined,
    Pause,
    PlayArrow,
    Settings,
    Stop
} from '@material-ui/icons';
import {Box, Button, useMediaQuery, Popover, Slider, Select, MenuItem, FormControl, InputLabel, FormControlLabel, Switch, Dialog, DialogContent, DialogActions} from "@mui/material";
import useStyles from '../../../assets/styles/ProgramPreviewStyles';
import {DQC_COLORS, PBDQ3_PROGRAM_SESSION_CONTENT_LAYOUT_VERBOSE} from "../../../utils/constants";
import {useHistory, useLocation, useParams} from "react-router-dom";
import AssessIntroImage from '../../../assets/images/Assess-Intro.png';
import {
    getInsiderProgramService,
    getLearningProgramPreviewService,
    getLearningProgramSlidePreviewService,
    getLearningProgramStatusService,
    listV3Level7DQCodeService,
    updateAssessmentVisibilityLearningProgramService
} from "../../../services/learning_programs.service";
import {toastWarning} from "../../../utils/utils";
import CancelIcon from '@material-ui/icons/Cancel';
import SlideComponentV2 from "./components/SlideComponentV2.comp";
import {AirbnbSlider, RazThumbComponent} from '../components/ProgressSlider.comp'


function useInterval(callback, delay) {
    const savedCallback = React.useRef();

    React.useEffect(() => {
        savedCallback.current = callback;
    }, [callback]);

    React.useEffect(() => {
        function tick() {
            savedCallback.current();
        }

        if (delay !== null) {
            const id = setInterval(tick, delay);
            return () => clearInterval(id);
        }
    }, [delay]);
}

// assessment viewer
const SessionAssessmentViewer = ({ title, items, currentQuestionIndex = 0, highlightWord }) => {
    const [questions, setQuestions] = useState([]);

    useEffect(() => {
        // Reset questions state first to ensure clean slate
        setQuestions([]);

        console.log('SessionAssessmentViewer: items changed', items);

        // Map raw items to question objects (including intro items)
        const mapped = (items || []).map((item, idx) => {
            // Check if this is an intro item (both pre and post)
            if (item.type === 'assessment_intro') {
                console.log('Mapping intro item:', item);
                return {
                    id: idx + 1,
                    type: 'assessment_intro',
                    title: item.title || '',
                    image_url: item.image_url || '',
                    layout: item.layout || '',
                    assessment_type: item.assessment_type || '',
                    content: item.content || '',
                };
            }

            // Regular assessment question
            let options = [];
            try {
                options = typeof item.instrument_response === 'string' ? JSON.parse(item.instrument_response) : item.instrument_response;
                if (!Array.isArray(options)) options = [options];
            } catch (e) {
                options = [];
            }
            return {
                id: idx + 1,
                type: item.category_11 || 'MCQ',
                instrument_question: item.instrument_question || '—',
                options,
                question_subtitle_top: item.question_subtitle_top,
                question_subtitle_bottom: item.question_subtitle_bottom,
                userAnswer: (item.category_11 === 'MAQ') ? [] : null,
            };
        });
        setQuestions(mapped);
    }, [items]);

    // Get current question from questions array - this will update when questions state changes
    const currentQuestion = useMemo(() => questions[currentQuestionIndex], [questions, currentQuestionIndex]);

    const isFreeTextQuestion = (q) => q && q.type === 'OPQ' && Array.isArray(q.options) && q.options.includes('Free text');

    const isOptionSelected = useCallback((option) => {
        if (!currentQuestion) return false;
        if (currentQuestion.type === 'MCQ' || currentQuestion.type === 'LSQ' || currentQuestion.type === 'YNQ') return currentQuestion.userAnswer === option;
        if (currentQuestion.type === 'MAQ') return currentQuestion.userAnswer?.includes(option);
        return false;
    }, [currentQuestion]);

    const handleOptionSelect = (option) => {
        console.log('Option selected:', option, 'Current index:', currentQuestionIndex, 'Total questions:', questions.length);
        setQuestions(prev => {
            const updated = prev.map((q, i) => {
                if (i !== currentQuestionIndex) return q;
                const updatedQuestion = { ...q };
                if (q.type === 'MCQ' || q.type === 'LSQ' || q.type === 'YNQ') {
                    updatedQuestion.userAnswer = option;
                } else if (q.type === 'MAQ') {
                    updatedQuestion.userAnswer = updatedQuestion.userAnswer?.includes(option)
                        ? updatedQuestion.userAnswer.filter(o => o !== option)
                        : [...(updatedQuestion.userAnswer || []), option];
                }
                console.log('Updated question:', updatedQuestion);
                return updatedQuestion;
            });
            console.log('All questions after update:', updated);
            return updated;
        });
    };

    return (
        <Box sx={{ width: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent:'center', height:'100%', }}>
            {/* Question block or Intro */}
            {currentQuestion ? (
                currentQuestion.type === 'assessment_intro' ? (
                    // Render intro content - image on left, title on right, content below
                    <Box sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center',
                        minHeight: '40vh',
                        padding: { xs: 2, sm: 4 },
                        width: '100%',
                        maxWidth: '1000px',
                        margin: '0 auto'
                    }}>
                        {/* Top section: Image on left, Title on right */}
                        <Box sx={{
                            display: 'flex',
                            flexDirection: { xs: 'column', md: 'row' },
                            alignItems: 'center',
                            justifyContent: 'center',
                            gap: { xs: 3, md: 4 },
                            width: '100%',
                            mb: currentQuestion.content ? 3 : 0
                        }}>
                            {/* Image section */}
                            <Box sx={{
                                flex: { xs: '0 0 auto', md: '0 0 45%' },
                                width: { xs: '100%', md: 'auto' },
                                maxWidth: { xs: '400px', md: '450px' },
                                display: 'flex',
                                justifyContent: 'center',
                                alignItems: 'center'
                            }}>
                                <img
                                    src={currentQuestion.image_url || AssessIntroImage}
                                    alt={currentQuestion.title || 'Assessment Intro'}
                                    style={{
                                        width: '65%',
                                        height: 'auto',
                                        borderRadius: '12px',
                                    }}
                                />
                            </Box>

                            {/* Title section */}
                            <Box sx={{
                                flex: { xs: '0 0 auto', md: '1' },
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center'
                            }}>
                                <Typography
                                    variant="h3"
                                    style={{
                                        color: '#fe696e',
                                        fontWeight: 700,
                                        textAlign: { xs: 'center', md: 'left' },
                                        fontSize: 'clamp(24px, 4vw, 36px)',
                                        lineHeight: 1.3
                                    }}
                                >
                                    {highlightWord ? highlightWord('intro_title', currentQuestion.title || 'Assessment Introduction') : (currentQuestion.title || 'Assessment Introduction')}
                                </Typography>
                            </Box>
                        </Box>

                        {/* Content section below (if available) */}
                        {currentQuestion.content && (
                            <Box sx={{
                                width: '100%',
                                mt: 3,
                                textAlign: 'center'
                            }}>
                                <Typography
                                    variant="body1"
                                    style={{
                                        color: '#555',
                                        fontSize: '18px',
                                        lineHeight: 1.6
                                    }}
                                >
                                    {highlightWord ? highlightWord('intro_content', currentQuestion.content) : currentQuestion.content}
                                </Typography>
                            </Box>
                        )}
                    </Box>
                ) : (
                    // Render regular question
                    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', minHeight:'40vh', justifyContent:'flex-start', }}>
                        <Box sx={{ width: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center', mt:'65px' }}>
                        {currentQuestion.question_subtitle_top && (
                            <Typography variant="h5" style={{ color: '#fe696e', fontWeight: 700, textAlign: 'center', marginBottom: 1 }}>
                            {highlightWord ? highlightWord('subtitle_top', currentQuestion.question_subtitle_top) : currentQuestion.question_subtitle_top}
                            </Typography>
                        )}
                        <Typography variant="h5" style={{ color: '#444', fontWeight: 700, textAlign: 'center' }}>
                        {highlightWord ? highlightWord('question', currentQuestion.instrument_question) : currentQuestion.instrument_question}
                        </Typography>
                        {currentQuestion.question_subtitle_bottom && (
                            <Typography variant="h5" style={{ color: '#0097b2', fontWeight: 700, textAlign: 'center', marginTop: 1 }}>
                            {highlightWord ? highlightWord('subtitle_bottom', currentQuestion.question_subtitle_bottom) : currentQuestion.question_subtitle_bottom}
                            </Typography>
                        )}
                     </Box>
                        {/* Options */}
                        <Box sx={{ mt: 4, width: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1, position: 'relative', zIndex: 10 }}>
                            {/* Render options in rows - basic responsive layout */}
                            <Box sx={{
                                display: 'flex',
                                justifyContent: 'center',
                                gap: '16px',
                                width: '100%',
                                flexWrap: 'wrap'
                            }}>
                                {(currentQuestion.options || []).map((opt, idx) => (
                                    <Box key={idx}
                                         onClick={(e) => {
                                             e.stopPropagation();
                                             console.log('Option clicked:', opt, 'Index:', idx);
                                             handleOptionSelect(opt);
                                         }}
                                         sx={{
                                             cursor: 'pointer',
                                             borderRadius: '8px',
                                             border: `2px solid ${isOptionSelected(opt) ? '#fe696e' : '#fe696e'}`,
                                             backgroundColor: isOptionSelected(opt) ? '#fe696e' : '#fff',
                                             color: isOptionSelected(opt) ? '#fff' : '#000',
                                             pointerEvents: 'auto',
                                             position: 'relative',
                                             zIndex: 10,
                                             transition: 'all 0.2s ease',
                                             flex: 1,
                                             display: 'flex',
                                             padding: '16px',
                                             minWidth: '125px',
                                             boxSizing: 'border-box',
                                             minHeight: '64px',
                                             textAlign: 'center',
                                             wordBreak: 'break-word',
                                             alignItems: 'center',
                                             whiteSpace: 'normal',
                                             justifyContent: 'center',
                                             '&:hover': {
                                                 backgroundColor: isOptionSelected(opt) ? '#fe696e' : '#fe696e',
                                                 color: '#fff',
                                                 transform: 'translateY(-1px)',
                                                 boxShadow: '0 4px 8px rgba(0, 0, 0, 0.1)',
                                             }
                                         }}>
                                        <Typography style={{
                                            fontSize: '16px',
                                            fontWeight: 500,
                                            lineHeight: 1.5,
                                        }}>{highlightWord ? highlightWord(`option_${idx}`, String(opt)) : String(opt)}</Typography>
                                    </Box>
                                ))}
                            </Box>
                        </Box>
                    </Box>
                )
            ) : (
                <Typography>No questions available.</Typography>
            )}
        </Box>
    );
};

const DQProProgPreview = () => {
    const classes = useStyles();
    const {code: dq_module_code} = useParams();
    const location = useLocation();
    const history = useHistory();
    const [programData, setProgramData] = useState(location.state?.assessment || null);
    const [modulesData, setModulesData] = useState([]);
    const [isContentLoaded, setIsContentLoaded] = useState(false);
    const [openModules, setOpenModules] = useState({});
    const [openUnits, setOpenUnits] = useState({});
    const [openSessions, setOpenSessions] = useState({});
    const [selectedSession, setSelectedSession] = useState(null);
    const [selectedSection, setSelectedSection] = useState(null);
    const [currentSlide, setCurrentSlide] = useState(0);
    const [loading, setLoading] = useState(false);
    const [isCollapsed, setIsCollapsed] = useState(false);
    const [sessionExists, setSessionExists] = useState(true);
    const [currentSegment, setCurrentSegment] = useState(0);
    const [contentStatus, setContentStatus] = useState({});
    const [sessionSlides, setSessionSlides] = useState({});
    const [activeSegment, setActiveSegment] = useState(null);
    const [currentSegmentSlides, setCurrentSegmentSlides] = useState([]);
    const [slidesLoading, setSlidesLoading] = useState(false);
    const [segments, setSegments] = useState([]);
    const [initialLoading, setInitialLoading] = useState(true);
    const [contentReady, setContentReady] = useState(false);
    const [dialogOpen, setDialogOpen] = useState(false);

    // Assessment visibility modal states
    const [assessmentModalOpen, setAssessmentModalOpen] = useState(false);
    const [assessmentModalType, setAssessmentModalType] = useState(null); // 'pre' or 'post'
    const [assessmentModalAction, setAssessmentModalAction] = useState(null); // 'hide' or 'unhide'
    const [assessmentVisibility, setAssessmentVisibility] = useState({}); // { session_code: { show_pre_assessment: bool, show_post_assessment: bool } }

    const [allSessionsDone, setAllSessionsDone] = useState(false);
    const [statusPolling, setStatusPolling] = useState(false);
    const [dqcoList, setDqcoList] = useState(location.state?.dqcoList || []);
    const lastStatusFetchTimeRef = React.useRef(null);
    const [popupContent, setPopupContent] = useState(null);
    const [ttsAuto, setTtsAuto] = useState(false);
    // STATES FOR NATIVE TTS
    const [speaking, setSpeaking] = useState(false);
    const [paused, setPaused] = useState(false);
    const [currentWordIndex, setCurrentWordIndex] = useState(-1);
    const utteranceRef = React.useRef(null);
    const speechWordsRef = React.useRef([]); // array of words (strings)
    const speechPartRangesRef = React.useRef({}); // id -> { start, end }
    const lastSpokenSlideTitleRef = React.useRef(null);
    const isMobile = useMediaQuery("(max-width:780px)");

    // NEW: store session assessments keyed by session_code
    const [sessionAssessments, setSessionAssessments] = useState({});

    // TTS SETTINGS STATE
    const [ttsRate, setTtsRate] = useState(1);
    const [ttsPitch, setTtsPitch] = useState(1);
    const [ttsVolume, setTtsVolume] = useState(1);
    const [availableVoices, setAvailableVoices] = useState([]);
    const [selectedVoiceURI, setSelectedVoiceURI] = useState('');
    const [ttsSettingsAnchor, setTtsSettingsAnchor] = useState(null);
    
    const TTS_TOAST_WARNING = 'Text to speech seem to have an issue with your browser. Please upgrade the browser version or try another browser.'

    // Load available voices (and refresh when they become available)
    useEffect(() => {
        const loadVoices = () => {
            try {
                const vs = window.speechSynthesis?.getVoices?.() || [];
                const filteredVoices = vs.filter(voice =>
                    voice.lang === 'en-US' || voice.lang === 'en-GB'
                );
                setAvailableVoices(filteredVoices);
            } catch (e) {
                setAvailableVoices([]);
            }
        };
        loadVoices();
        if (window && window.speechSynthesis) {
            window.speechSynthesis.addEventListener('voiceschanged', loadVoices);
        }
        return () => {
            if (window && window.speechSynthesis) {
                window.speechSynthesis.removeEventListener('voiceschanged', loadVoices);
            }
        };
    }, []);

    const currentSessionSlide = useMemo(() => (
        currentSegmentSlides && currentSegmentSlides.length > 0
            ? currentSegmentSlides[currentSlide]
            : null
    ), [currentSegmentSlides, currentSlide]);

    // Determine if we should skip title speaking (consecutive same titles)
    const skipTitleSpeech = useMemo(() => {
        if (!currentSegmentSlides || currentSegmentSlides.length === 0) return false;
        const slide = currentSegmentSlides[currentSlide];
        console.log('currentSlide:', currentSlide, 'slide:', slide);
        const title = slide?.SlideTitle || '';
        const prevSlide = currentSlide > 0 ? currentSegmentSlides[currentSlide - 1] : null;
        console.log('skipTitleSpeech check:', {title, prevTitle: prevSlide?.SlideTitle});
        return prevSlide ? prevSlide.SlideTitle === title : lastSpokenSlideTitleRef.current === title;
    }, [currentSegmentSlides, currentSlide]);

    // BUILD SPEECH PARTS AND WORD MAPPING
    const prepareSpeechForSlide = useCallback((slide) => {
        speechWordsRef.current = [];
        speechPartRangesRef.current = {};
        if (!slide) return '';

        // Handle assessment slides
        if (slide.kind === 'ASSESSMENT') {
            const parts = [];
            // Get the assessment question data
            const sessKey = selectedSession?.session_code;
            const assess = sessionAssessments?.[sessKey] || {};
            const assessmentItems = activeSegment === '__PRE_ASSESSMENT__' ? (assess.pre || []) : (assess.post || []);
            const questionData = assessmentItems[slide.questionIndex];

            if (questionData) {
                if (questionData.type === 'assessment_intro') {
                    // Handle intro slide - read title and content
                    if (questionData.title) {
                        parts.push({id: 'intro_title', text: String(questionData.title) + '.'});
                    }
                    if (questionData.content) {
                        parts.push({id: 'intro_content', text: String(questionData.content) + '.'});
                    }
                } else {
                    // Handle regular assessment question
                    // Add question subtitles and main question text
                    if (questionData.question_subtitle_top) {
                        parts.push({id: 'subtitle_top', text: String(questionData.question_subtitle_top) + '.'});
                    }
                    if (questionData.instrument_question) {
                        parts.push({id: 'question', text: String(questionData.instrument_question) + '.'});
                    }
                    if (questionData.question_subtitle_bottom) {
                        parts.push({id: 'subtitle_bottom', text: String(questionData.question_subtitle_bottom) + '.'});
                    }

                    // Add options
                    let options = [];
                    try {
                        options = typeof questionData.instrument_response === 'string'
                            ? JSON.parse(questionData.instrument_response)
                            : questionData.instrument_response;
                        if (!Array.isArray(options)) options = [options];
                    } catch (e) {
                        options = [];
                    }

                    if (options && options.length > 0) {
                        options.forEach((opt, idx) => {
                            if (opt && String(opt).trim()) {
                                parts.push({id: `option_${idx}`, text: String(opt)});
                            }
                        });
                    }
                }
            }

            // Build combined text & ranges for assessment
            let globalWordIndex = 0;
            parts.forEach(p => {
                const words = p.text.match(/[^\s]+/g) || [];
                const start = globalWordIndex;
                const end = start + words.length - 1;
                speechPartRangesRef.current[p.id] = {start, end};
                speechWordsRef.current.push(...words);
                globalWordIndex = end + 1;
            });
            return parts.map(p => p.text).join('. ');
        }

        // Handle regular slides
        const parts = [];
        if (slide.SlideTitle && !skipTitleSpeech) {
            // Add pause after title
            parts.push({id: 'title', text: String(slide.SlideTitle) + '.'});
        }
        const pushContentString = (text, idBase, isSubHeading = false) => {
            if (typeof text === 'string' && text.trim().length) {
                // Add pause after sub-headings (titles in step blocks)
                const processedText = isSubHeading ? text + '.' : text;
                parts.push({id: idBase, text: processedText});
            }
        };
        const sc = slide.SlideContent;
        if (typeof sc === 'string') {
            pushContentString(sc, 'content_0');
        } else if (Array.isArray(sc)) {
            sc.forEach((item, idx) => {
                if (typeof item === 'string') {
                    pushContentString(item, `content_${idx}`);
                } else if (item && typeof item === 'object') {
                    // Step blocks or objects with Title/Text
                    if (item.Title) pushContentString(item.Title, `content_${idx}_title`, true); // Mark as sub-heading
                    if (item.Text) pushContentString(item.Text, `content_${idx}_text`);
                }
            });
        } else if (sc && typeof sc === 'object') {
            Object.entries(sc).forEach(([k, v]) => {
                if (typeof v === 'string') pushContentString(v, `content_obj_${k}`);
            });
        }
        // Build combined text & ranges
        let globalWordIndex = 0;
        parts.forEach(p => {
            const words = p.text.match(/[^\s]+/g) || [];
            const start = globalWordIndex;
            const end = start + words.length - 1;
            speechPartRangesRef.current[p.id] = {start, end};
            speechWordsRef.current.push(...words);
            globalWordIndex = end + 1;
        });
        // Join parts with additional pause markers for better speech flow
        return parts.map(p => p.text).join('. ');
    }, [skipTitleSpeech, sessionAssessments, selectedSession, activeSegment]);

    const speechText = useMemo(() => prepareSpeechForSlide(currentSessionSlide), [currentSessionSlide, prepareSpeechForSlide]);

    // CLEAN UP ON UNMOUNT
    useEffect(() => {
        return () => {
            window.speechSynthesis.cancel();
        };
    }, []);

    const startSpeech = useCallback(() => {
        if (!speechText) return;

        // Check if speech synthesis is supported
        if (!('speechSynthesis' in window) || !window.speechSynthesis) {
            toastWarning(TTS_TOAST_WARNING);
            return;
        }

        // Cancel any existing speech
        window.speechSynthesis.cancel();

        const utter = new SpeechSynthesisUtterance(speechText);
        utter.lang = 'en-US';
        utter.rate = ttsRate;
        utter.pitch = ttsPitch;
        utter.volume = ttsVolume;

        // Set selected voice if chosen
        const allVoices = window.speechSynthesis.getVoices();
        // Filter to only include en-US and en-GB voices (consistent with availableVoices filtering)
        const voices = allVoices.filter(voice =>
            voice.lang === 'en-US' || voice.lang === 'en-GB'
        );
        console.log('Available voices:', voices.map(v => ({name: v.name, lang: v.lang, gender: v.name.toLowerCase()})));

        // // Additional check: if no voices are available, it might indicate TTS issues
        // if (voices.length === 0) {
        //     // Wait a bit and check again, sometimes voices load asynchronously
        //     setTimeout(() => {
        //         const retryVoices = window.speechSynthesis.getVoices();
        //         if (retryVoices.length === 0) {
        //             toastWarning(TTS_TOAST_WARNING);
        //             return;
        //         }
        //         // If voices are now available, restart the function
        //         startSpeech();
        //     }, 500);
        //     return;
        // }

        if (voices.length > 0) {
            let chosen = null;
            if (selectedVoiceURI) {
                chosen = voices.find(v => v.voiceURI === selectedVoiceURI) || null;
            }
            if (!chosen) {
                // Try to find a female voice with improved detection
                const femaleVoice = voices.find(voice => {
                    const name = voice.name.toLowerCase();
                    const lang = voice.lang.toLowerCase();

                    // Only consider English voices
                    if (!lang.startsWith('en')) return false;

                    return (
                        // Explicit female indicators
                        name.includes('female') ||
                        // Common female voice names across platforms
                        name.includes('zira') ||      // Windows
                        name.includes('susan') ||     // Windows
                        name.includes('samantha') ||  // macOS
                        name.includes('karen') ||     // macOS
                        name.includes('anna') ||      // Various
                        name.includes('marie') ||     // Various
                        name.includes('serena') ||    // Various
                        name.includes('alexia') ||    // Various
                        name.includes('tessa') ||     // Various
                        name.includes('alice') ||     // Chrome/Android
                        name.includes('victoria') ||  // Various
                        name.includes('celia') ||     // macOS Spanish but sometimes available
                        name.includes('amelie') ||    // Various
                        name.includes('ellen') ||     // Various
                        // Default system female voices
                        (name.includes('english') && (name.includes('female') || name.includes('united states') || name.includes('us')))
                    );
                });
                chosen = femaleVoice || null;
                if (!chosen) {
                    // Fallback: try to get any English voice and prefer the second one if available (often female on Windows)
                    const englishVoices = voices.filter(voice => voice.lang.startsWith('en'));
                    console.log('English voices available:', englishVoices.map(v => v.name));

                    if (englishVoices.length >= 2) {
                        console.log('Using second English voice as fallback:', englishVoices[1].name);
                        chosen = englishVoices[1];
                    } else if (englishVoices.length > 0) {
                        console.log('Using first English voice as fallback:', englishVoices[0].name);
                        chosen = englishVoices[0];
                    }
                }
            }
            if (chosen) {
                utter.voice = chosen;
            } else {
                console.warn('No voices available yet, using default voice');
            }
        } else {
            console.warn('No voices available yet, using default voice');
        }

        setCurrentWordIndex(-1);
        speechWordsRef.current = speechText.match(/[^\s]+/g) || [];

        // Set up event handlers before starting speech
        utter.onstart = () => {
            setSpeaking(true);
            setPaused(false);
        };

        utter.onboundary = (e) => {
            if (e.name === 'word' || e.charIndex != null) {
                // Approximate word index by counting words before charIndex
                if (e.charIndex != null) {
                    let count = 0;
                    const textUpToChar = speechText.slice(0, e.charIndex);
                    const arr = textUpToChar.match(/[^\s]+/g);
                    if (arr) count = arr.length;
                    setCurrentWordIndex(count);
                }
            }
        };

        utter.onend = () => {
            setSpeaking(false);
            setPaused(false);
            setCurrentWordIndex(-1);
            if (!skipTitleSpeech && currentSessionSlide?.SlideTitle) {
                lastSpokenSlideTitleRef.current = currentSessionSlide.SlideTitle;
            }
        };

        utter.onerror = (event) => {
            console.error('Speech synthesis error:', event);
            setSpeaking(false);
            setPaused(false);
            setCurrentWordIndex(-1);
            // // Show toast warning when speech synthesis fails
            // if(event.error === 'canceled') {
            //     toastWarning(TTS_TOAST_WARNING);
            // }
        };

        utteranceRef.current = utter;

        // Try to start speech synthesis
        try {
            window.speechSynthesis.speak(utter);

            // Set a timeout to check if speech actually started
            setTimeout(() => {
                if (!window.speechSynthesis.speaking && !window.speechSynthesis.pending) {
                    // Speech didn't start, reset states
                    setSpeaking(false);
                    setPaused(false);
                    setCurrentWordIndex(-1);
                    console.warn('Speech synthesis failed to start');
                }
            }, 250);

        } catch (error) {
            console.error('Failed to start speech synthesis:', error);
            setSpeaking(false);
            setPaused(false);
            setCurrentWordIndex(-1);
            // toastWarning(TTS_TOAST_WARNING);
        }
    }, [speechText, currentSessionSlide, skipTitleSpeech, ttsRate, ttsPitch, ttsVolume, selectedVoiceURI]);

    const pauseSpeech = useCallback(() => {
        if (!speaking) return;
        if (!paused) {
            window.speechSynthesis.pause();
            setPaused(true);
        } else {
            window.speechSynthesis.resume();
            setPaused(false);
        }
    }, [speaking, paused]);

    const stopSpeech = useCallback(() => {
        window.speechSynthesis.cancel();
        setSpeaking(false);
        setPaused(false);
        setCurrentWordIndex(-1);
    }, []);

    // HANDLE AUTO PLAY
    useEffect(() => {
        if (ttsAuto) {
            stopSpeech();
            if (speechText) {
                startSpeech();
            }
        } else {
            stopSpeech();
        }
    }, [ttsAuto, currentSessionSlide, speechText, startSpeech, stopSpeech]);

    // If settings change while speaking with autoplay, restart speech
    useEffect(() => {
        if (ttsAuto && speaking) {
            stopSpeech();
            startSpeech();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [ttsRate, ttsPitch, ttsVolume, selectedVoiceURI]);

    // HIGHLIGHT HELPERS PASSED TO SLIDE COMPONENT
    const highlightWordRenderer = useCallback((id, rawText) => {
        if (rawText == null) return null;
        // Normalize rawText to string
        let normalized = rawText;
        if (Array.isArray(normalized)) {
            normalized = normalized
                .map(v => (typeof v === 'string' ? v : (v && typeof v === 'object' ? Object.values(v).filter(x => typeof x === 'string').join(' ') : '')))
                .filter(Boolean)
                .join(' ');
        } else if (typeof normalized === 'object') {
            normalized = Object.values(normalized).filter(v => typeof v === 'string').join(' ');
        }
        if (typeof normalized !== 'string') normalized = String(normalized);
        if (!normalized.trim()) return null;

        const range = speechPartRangesRef.current[id];
        const words = normalized.match(/[^\s]+/g) || [];
        if (!range) {
            return words.map((w, i) => <span key={`${id}-w-${i}`}>{w + ' '}</span>);
        }
        const {start} = range;
        return words.map((w, i) => {
            const globalIndex = start + i;
            const isCurrent = globalIndex === currentWordIndex;
            return <span key={`${id}-w-${i}`} style={isCurrent ? {
                backgroundColor: '#FFF3CD',
                color: '#000',
                borderRadius: '4px'
            } : {}}>{w + ' '}</span>;
        });
    }, [currentWordIndex]);

    useEffect(() => {
        const fetchProgramData = async () => {
            try {
                if (!programData && dq_module_code) {
                    setLoading(true);
                    const response = await getInsiderProgramService(dq_module_code);
                    if (response?.data?.learning_program) {
                        setProgramData(response.data.learning_program);
                        console.log('program: ', response.data.learning_program);
                    }
                }
            } catch (error) {
                console.error('Error fetching program data:', error);
            } finally {
                setLoading(false);
            }
        };

        fetchProgramData().then(r => console.log(r));
    }, [programData, dq_module_code]);

    // Helper to build segments list with optional Pre/Post Assessment entries
    const buildSegmentsList = useCallback((slideData, session, sessionAssessmentsOverride = null) => {
        const base = Object.keys(slideData || {})
            // .filter(segmentKey => segmentKey !== 'IntroVideo')
            .map(segmentKey => ({ id: segmentKey, name: getSegmentDisplayName(segmentKey, session), kind: 'slides' }));
        const sessKey = session?.session_code;
        // Use the override if provided, otherwise fall back to state
        const assessData = sessionAssessmentsOverride !== null ? sessionAssessmentsOverride : sessionAssessments;
        const assess = assessData?.[sessKey] || {};
        const hasPre = Array.isArray(assess.pre) && assess.pre.length > 0;
        const hasPost = Array.isArray(assess.post) && assess.post.length > 0;
        let list = [...base];
        // Insert Pre after Introduction (Seg1) if exists, else at start
        if (hasPre) {
            const idxIntro = list.findIndex(s => s.id === 'Seg1');
            const insertIdx = idxIntro >= 0 ? idxIntro + 1 : 0;
            list.splice(insertIdx, 0, { id: '__PRE_ASSESSMENT__', name: 'Pre Assessment', kind: 'assessment', assessType: 'pre' });
        }
        // Insert Post before Recap (Seg5) if exists, else at the end
        if (hasPost) {
            const idxRecap = list.findIndex(s => s.id === 'Seg5');
            if (idxRecap >= 0) {
                // Insert before Recap
                list.splice(idxRecap, 0, { id: '__POST_ASSESSMENT__', name: 'Post Assessment', kind: 'assessment', assessType: 'post' });
            } else {
                // No Recap found, append at the end
                list.push({ id: '__POST_ASSESSMENT__', name: 'Post Assessment', kind: 'assessment', assessType: 'post' });
            }
        }
        return list;
    }, [sessionAssessments]);

    useEffect(() => {
        const fetchModules = async () => {
            if (!dq_module_code) {
                setModulesData([]);
                setLoading(false);
                setInitialLoading(false);
                return;
            }

            try {
                setLoading(true);
                const res = await getLearningProgramPreviewService(dq_module_code);
                if (res?.data?.program_layout_preview) {
                    console.log('outline: ', res.data.program_layout_preview)
                    const program = res?.data?.program_layout_preview || {};
                    // NEW: capture session assessments if provided
                    const sessAssess = res?.data?.session_assessments || {};
                    console.log('assessments: ', sessAssess);
                    setSessionAssessments(sessAssess);

                    if (!program || !Array.isArray(program.modules)) {
                        setModulesData([]);
                        setLoading(false);
                        setInitialLoading(false);
                        return;
                    }

                    const mappedModules = program.modules.map(mod => ({
                        module_code: mod.module_code || '',
                        module_name: mod.module_title || '',
                        module_description: mod.description_for_educator || '',
                        module_duration: mod.total_duration_minutes || '',
                        module_items_definitions: {
                            title: mod.definition_title || '',
                            description: mod.definition || ''
                        },
                        unique_dqco: mod.unique_dqco || [],
                        units: (mod.units || []).map(unit => ({
                            unit_code: unit.unit_code || '',
                            unit_title: unit.unit_title || '',
                            unit_description: unit.description_for_educator || '',
                            unit_duration: unit.total_duration_minutes || 0,
                            lessons: (unit.sessions || []).map(session => ({
                                session_code: session.session_code || '',
                                lesson_title: session.session_title || '',
                                lesson_duration: session.duration_minutes || 0,
                                lesson_description: session.description_for_educator || '',
                                lesson_tokens: session.lesson_tokens || [],
                                session_learning_objectives: session.session_learning_objectives || [],
                            })),
                        })),
                    }));

                    setModulesData(mappedModules);

                    // LAZY LOADING: Find first available session with 'done' status
                    if (mappedModules.length > 0) {
                        let firstAvailableSession = null;
                        let moduleIdx = -1;
                        let unitIdx = -1;

                        // Iterate through modules and units to find first ready session
                        outerLoop: for (let mIdx = 0; mIdx < mappedModules.length; mIdx++) {
                            const module = mappedModules[mIdx];
                            if (!module.units || module.units.length === 0) continue;

                            for (let uIdx = 0; uIdx < module.units.length; uIdx++) {
                                const unit = module.units[uIdx];
                                if (!unit.lessons || unit.lessons.length === 0) continue;

                                for (const session of unit.lessons) {
                                    // Check if this session is ready (will check contentStatus once it's loaded)
                                    firstAvailableSession = session;
                                    moduleIdx = mIdx;
                                    unitIdx = uIdx;
                                    break outerLoop;
                                }
                            }
                        }

                        if (firstAvailableSession && moduleIdx >= 0 && unitIdx >= 0) {
                            // Auto-open the module and unit containing the first session
                            setOpenModules(prev => ({...prev, [moduleIdx]: true}));
                            setOpenUnits(prev => ({...prev, [`${moduleIdx}-${unitIdx}`]: true}));

                            // Delay to allow contentStatus to be fetched first
                            setTimeout(() => {
                                fetchFirstSessionContent(firstAvailableSession, sessAssess);
                            }, 1000);
                        } else {
                            setInitialLoading(false);
                        }
                    } else {
                        setInitialLoading(false);
                    }
                }
            } catch (err) {
                console.error('Error fetching modules:', err);
                setModulesData([]);
                setInitialLoading(false);
            } finally {
                setLoading(false);
            }
        };

        fetchModules().then(r => console.log(r));
    }, [dq_module_code]);

    const getSloTitlesForSession = (session) => {
        // Handles both old and new data shape, expects array of SLOs
        if (!session?.session_learning_objectives) return {};
        const sloTitles = {};
        session.session_learning_objectives.forEach((slo, idx) => {
            sloTitles[`SLO${idx + 1}`] = slo.slo_title || `SLO${idx + 1}`;
        });
        return sloTitles;
    };


    const getSegmentDisplayName = (segmentKey, session) => {
        if (segmentKey.startsWith("SLO")) {
            // Dynamically fetch SLO title from session object
            const sloTitles = getSloTitlesForSession(session);
            return sloTitles[segmentKey] || segmentKey;
        }

        const staticNames = {
            "Seg1": "Introduction",
            "Seg4": "Reflection",
            "Seg5": "Recap",
            "Seg5_1": "Module Score"
        };
        return staticNames[segmentKey] || segmentKey;
    };

    const fetchFirstSessionContent = useCallback(async (session, sessionAssessmentsOverride = null) => {
        if (!session || !programData?.target_audience) {
            setInitialLoading(false);
            return;
        }

        // Check if session is ready - for lazy loading, we'll find the first available session
        const sessionStatusData = contentStatus[session.session_code];
        const sessionStatus = typeof sessionStatusData === 'object' ? sessionStatusData.status : sessionStatusData;
        if (sessionStatus && sessionStatus !== 'done') {
            // Don't show toast during initial load - we'll handle lazy loading silently
            console.log(`Session "${session.lesson_title}" is ${sessionStatus === 'in_progress' ? 'still generating' : 'not available'}.`);
            setInitialLoading(false);
            return;
        }

        setSlidesLoading(true);
        try {
            const result = await getLearningProgramSlidePreviewService(
                programData.code,
                session.session_code
            );

            if (result?.data?.slide_preview) {
                const slideData = result.data.slide_preview;
                // const introVideo = slideData.IntroVideo;
                // remove keys CourseCode & session_code from slideData
                console.log('First slide data:', slideData);
                // console.log('IntroVideo:', introVideo);

                // Capture assessment visibility data
                if (result?.data?.show_pre_assessment !== undefined || result?.data?.show_post_assessment !== undefined) {
                    setAssessmentVisibility(prev => ({
                        ...prev,
                        [session.session_code]: {
                            show_pre_assessment: result.data.show_pre_assessment ?? true,
                            show_post_assessment: result.data.show_post_assessment ?? true
                        }
                    }));
                }

                // // If IntroVideo exists, add it as the first slide in Seg1 (Introduction)
                // if (introVideo && slideData['Seg1']) {
                //     const videoSlide = {
                //         SlideLayout: 'VideoTemplate',
                //         SlideTitle: '',
                //         SlideContent: '',
                //         VideoUrl: introVideo,
                //         Images: []
                //     };
                //
                //     // Prepend video slide to Seg1
                //     slideData['Seg1'] = [videoSlide, ...slideData['Seg1']];
                // }

                setSessionSlides(slideData);
                setOpenSessions(prev => ({...prev, [session.session_code]: true}));

                // Build segments augmented with assessments - pass override if provided
                const segmentsList = buildSegmentsList(slideData, session, sessionAssessmentsOverride);
                setSegments(segmentsList);

                if (segmentsList.length > 0) {
                    setSelectedSession(session);
                    setSelectedSection('Session Details');
                    setActiveSegment(segmentsList[0].id);
                    setCurrentSegment(0);
                }
            }
        } catch (err) {
            console.error("Error fetching first session slides:", err);
        } finally {
            setSlidesLoading(false);
            setInitialLoading(false);
        }
    }, [programData, contentStatus, buildSegmentsList]);

    // LAZY LOADING: Once contentStatus is available, load first ready session
    useEffect(() => {
        // Only run if we have contentStatus and haven't loaded a session yet
        if (Object.keys(contentStatus).length === 0 || selectedSession) {
            return;
        }

        if (programData?.target_audience && modulesData.length > 0) {
            // Find first session with 'done' status
            let firstReadySession = null;
            let moduleIdx = -1;
            let unitIdx = -1;

            outerLoop: for (let mIdx = 0; mIdx < modulesData.length; mIdx++) {
                const module = modulesData[mIdx];
                if (!module.units || module.units.length === 0) continue;

                for (let uIdx = 0; uIdx < module.units.length; uIdx++) {
                    const unit = module.units[uIdx];
                    if (!unit.lessons || unit.lessons.length === 0) continue;

                    for (const session of unit.lessons) {
                        const sessionStatusData = contentStatus[session.session_code];
                        const sessionStatus = typeof sessionStatusData === 'object' ? sessionStatusData.status : sessionStatusData;
                        if (sessionStatus === 'done') {
                            firstReadySession = session;
                            moduleIdx = mIdx;
                            unitIdx = uIdx;
                            break outerLoop;
                        }
                    }
                }
            }

            if (firstReadySession && moduleIdx >= 0 && unitIdx >= 0) {
                console.log('Lazy loading first ready session:', firstReadySession.lesson_title);
                // Auto-open the module and unit containing the first ready session
                setOpenModules(prev => ({...prev, [moduleIdx]: true}));
                setOpenUnits(prev => ({...prev, [`${moduleIdx}-${unitIdx}`]: true}));

                // Load the first ready session
                fetchFirstSessionContent(firstReadySession);
            } else {
                // No ready sessions yet, stop initial loading
                console.log('No ready sessions found yet, waiting for content generation...');
                setInitialLoading(false);
            }
        }
    }, [contentStatus, programData, modulesData, selectedSession, fetchFirstSessionContent]);

    const fetchContentStatus = useCallback(async () => {
        try {
            const now = Date.now();
            const timeSinceLastFetch = lastStatusFetchTimeRef.current ? now - lastStatusFetchTimeRef.current : null;

            if (timeSinceLastFetch !== null) {
                console.log(`Fetching content status (${(timeSinceLastFetch / 1000).toFixed(1)}s since last fetch)`);
            } else {
                console.log('Fetching content status (initial fetch)');
            }

            const res = await getLearningProgramStatusService(dq_module_code);
            lastStatusFetchTimeRef.current = Date.now();

            if (res?.data?.contentStatus) {
                const newStatuses = res.data.contentStatus;
                console.log('contentStatus: ', newStatuses);

                // Check for newly completed sessions using functional update
                setContentStatus(prevStatuses => {
                    const newlyCompleted = [];

                    Object.keys(newStatuses).forEach(sessionCode => {
                        const prevStatusData = prevStatuses[sessionCode];
                        const newStatusData = newStatuses[sessionCode];

                        const prevStatus = typeof prevStatusData === 'object' ? prevStatusData.status : prevStatusData;
                        const newStatus = typeof newStatusData === 'object' ? newStatusData.status : newStatusData;

                        if (prevStatus === 'in_progress' && newStatus === 'done') {
                            newlyCompleted.push(sessionCode);
                            console.log('Session newly completed:', sessionCode);
                        }
                    });

                    // If no session is currently loaded and we have newly completed sessions, load the first one
                    if (!selectedSession && newlyCompleted.length > 0 && modulesData.length > 0) {
                        // Find the first newly completed session in the module structure
                        let foundSession = false;
                        for (const module of modulesData) {
                            if (!module.units || foundSession) continue;
                            for (const unit of module.units) {
                                if (!unit.lessons || foundSession) continue;
                                for (const session of unit.lessons) {
                                    if (newlyCompleted.includes(session.session_code)) {
                                        console.log('Auto-loading newly completed session:', session.lesson_title);
                                        fetchFirstSessionContent(session);
                                        foundSession = true;
                                        break;
                                    }
                                }
                            }
                        }
                    }

                    return newStatuses;
                });

                // Check if any sessions are still in progress
                const anyInProgress = Object.values(newStatuses).some(statusData => {
                    const status = typeof statusData === 'object' ? statusData.status : statusData;
                    return status === 'in_progress';
                });
                setAllSessionsDone(!anyInProgress);

                // Stop polling if none are in progress
                if (!anyInProgress) {
                    console.log('No sessions in progress, stopping polling');
                    setStatusPolling(false);
                }
            }
        } catch (err) {
            console.error('Error fetching content status:', err);
            setContentStatus({});
        }
    }, [dq_module_code, selectedSession, modulesData, fetchFirstSessionContent]);

    // Initial content status fetch with delay and start polling
    useEffect(() => {
        if (!dq_module_code) return;

        // Add 5-second delay before first fetch
        const timer = setTimeout(() => {
            console.log('Starting content status polling...');
            fetchContentStatus();
            setStatusPolling(true);
        }, 5000);

        return () => clearTimeout(timer);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [dq_module_code]);

    // Polling interval for status updates - maintains 5-second minimum interval
    useInterval(() => {
        if (statusPolling) {
            const now = Date.now();
            const timeSinceLastFetch = lastStatusFetchTimeRef.current ? now - lastStatusFetchTimeRef.current : null;

            // Ensure at least 5 seconds have passed since last fetch
            if (timeSinceLastFetch === null || timeSinceLastFetch >= 5000) {
                fetchContentStatus();
            } else {
                const remainingTime = 5000 - timeSinceLastFetch;
                console.log(`Skipping fetch, ${(remainingTime / 1000).toFixed(1)}s remaining until next allowed fetch`);
            }
        }
    }, statusPolling ? 5000 : null);

    useEffect(() => {
        if (activeSegment && (sessionSlides[activeSegment] || activeSegment === '__PRE_ASSESSMENT__' || activeSegment === '__POST_ASSESSMENT__')) {
            // Clear the last spoken title ref when changing segments to ensure first slide reads out
            lastSpokenSlideTitleRef.current = null;

            if (activeSegment === '__PRE_ASSESSMENT__' || activeSegment === '__POST_ASSESSMENT__') {
                // For assessment segments, create one slide per question
                const sessKey = selectedSession?.session_code;
                const assess = sessionAssessments?.[sessKey] || {};
                const assessmentItems = activeSegment === '__PRE_ASSESSMENT__' ? (assess.pre || []) : (assess.post || []);
                // Create a slide object for each assessment question
                const assessmentSlides = assessmentItems.map((item, idx) => ({ kind: 'ASSESSMENT', questionIndex: idx }));
                setCurrentSegmentSlides(assessmentSlides);
                setCurrentSlide(0);
            } else {
                const slides = Array.isArray(sessionSlides[activeSegment])
                    ? sessionSlides[activeSegment]
                    : [sessionSlides[activeSegment]];
                setCurrentSegmentSlides(slides);
                setCurrentSlide(0);
            }
        } else {
            setCurrentSegmentSlides([]);
        }
    }, [activeSegment, sessionSlides, sessionAssessments, selectedSession]);

    const toggleCollapse = () => {
        setIsCollapsed(!isCollapsed);
    };

    const handleToggleModuleDropdown = (moduleIndex) => {
        setOpenModules(prev => ({
            ...prev,
            [moduleIndex]: !prev[moduleIndex]
        }));
    };

    const handleToggleUnitDropdown = (moduleIndex, unitIndex) => {
        setOpenUnits(prev => ({
            ...prev,
            [`${moduleIndex}-${unitIndex}`]: !prev[`${moduleIndex}-${unitIndex}`]
        }));
    };

    const handleToggleSessionDropdown = (session) => {
        const sessionCode = session.session_code;
        const sessionStatusData = contentStatus[sessionCode];
        const sessionStatus = typeof sessionStatusData === 'object' ? sessionStatusData.status : sessionStatusData;

        // Don't allow toggling for failed/halted sessions
        if (sessionStatus === 'failed') {
            toastWarning(`Session "${session.lesson_title}" is not available.`);
            return;
        }

        // For in_progress sessions, show notification
        if (sessionStatus === 'in_progress') {
            toastWarning(`Session "${session.lesson_title}" is still generating. Please wait.`);
            return;
        }

        // For done sessions, proceed normally
        // Close other sessions if this one is being opened
        if (!openSessions[sessionCode]) {
            // Create a new object with all sessions closed except the current one
            const newOpenSessions = {};
            Object.keys(openSessions).forEach(key => { newOpenSessions[key] = false; });
            newOpenSessions[sessionCode] = true;
            setOpenSessions(newOpenSessions);

            // Clear current segments when switching sessions
            if (selectedSession?.session_code !== sessionCode) {
                setSegments([]);
                setCurrentSegmentSlides([]);
                setActiveSegment(null);
            }

            // Fetch the slides for this session
            fetchSessionSlides(session);
        } else {
            // Toggle the current session closed if it was open
            setOpenSessions(prev => ({
                ...prev,
                [sessionCode]: !prev[sessionCode]
            }));
        }
    };

    const fetchSessionSlides = async (session, startAtLastSegment = false) => {
        if (!session) return;

        setSlidesLoading(true);
        setSelectedSession(session); // Set the selected session immediately

        try {
            const result = await getLearningProgramSlidePreviewService(
                programData?.code,
                session.session_code
            );

            if (result?.data?.slide_preview) {
                const slideData = result.data.slide_preview;
                // const introVideo = slideData.IntroVideo;
                // remove keys CourseCode & session_code from slideData
                console.log('Slide data:', slideData);
                // console.log('IntroVideo:', introVideo);

                // Capture assessment visibility data
                if (result?.data?.show_pre_assessment !== undefined || result?.data?.show_post_assessment !== undefined) {
                    setAssessmentVisibility(prev => ({
                        ...prev,
                        [session.session_code]: {
                            show_pre_assessment: result.data.show_pre_assessment ?? true,
                            show_post_assessment: result.data.show_post_assessment ?? true
                        }
                    }));
                }

                // // If IntroVideo exists, add it as the first slide in Seg1 (Introduction)
                // if (introVideo && slideData['Seg1']) {
                //     const videoSlide = {
                //         SlideLayout: 'VideoTemplate',
                //         SlideTitle: '',
                //         SlideContent: '',
                //         VideoUrl: introVideo,
                //         Images: []
                //     };
                //
                //     // Prepend video slide to Seg1
                //     slideData['Seg1'] = [videoSlide, ...slideData['Seg1']];
                // }

                // Store the raw slide data
                setSessionSlides(slideData);
                const segmentsList = buildSegmentsList(slideData, session);
                setSegments(segmentsList);

                // Automatically select the first or last segment based on parameter
                if (segmentsList.length > 0) {
                    if (startAtLastSegment) {
                        const lastSegment = segmentsList[segmentsList.length - 1];
                        setActiveSegment(lastSegment.id);
                        const lastSegmentSlides = slideData[lastSegment.id] || [];
                        setCurrentSlide(lastSegmentSlides.length - 1 || 0);
                        setSelectedSection('Session Details');
                        setCurrentSegment(segmentsList.length - 1);
                    } else {
                        setActiveSegment(segmentsList[0].id);
                        setCurrentSlide(0);
                        setSelectedSection('Session Details');
                        setCurrentSegment(0);
                    }
                }
            } else {
                setSessionSlides({});
                setSegments([]);
            }
        } catch (err) {
            setSessionSlides({});
            setSegments([]);
            console.error("Error fetching slides:", err);
        } finally {
            setSlidesLoading(false);
        }
    };

    const handleViewSegment = (session, segmentId) => {
        setSelectedSession(session);
        setSelectedSection('Session Details');
        setActiveSegment(segmentId);
        setCurrentSlide(0);

        // Find the index of the selected segment
        const segmentIndex = segments.findIndex(seg => seg.id === segmentId);
        if (segmentIndex !== -1) {
            setCurrentSegment(segmentIndex);
        }
    };

    const handleNextSlide = () => {
        if (currentSlide < currentSegmentSlides.length - 1) {
            setCurrentSlide(prev => prev + 1);
        } else if (currentSegment < segments.length - 1) {
            // Move to the next segment if at the end of current segment slides
            handleNextSegment();
        }
    };

    const handlePrevSlide = () => {
        if (currentSlide > 0) {
            setCurrentSlide(prev => prev - 1);
        } else {
            // At the beginning of current segment slides, try to move to previous segment or session
            handlePreviousSegment();
        }
    };

    const handlePreviousSegment = () => {
        if (segments.length === 0) return;

        const currentIndex = segments.findIndex(seg => seg.id === activeSegment);

        // Check if we can move to the previous segment within current session
        if (currentIndex > 0) {
            const prevSegment = segments[currentIndex - 1];
            setActiveSegment(prevSegment.id);
            setCurrentSlide(sessionSlides[prevSegment.id]?.length - 1 || 0); // Go to last slide of previous segment
            setCurrentSegment(currentIndex - 1);
        } else if (currentIndex === 0) {
            // We're at the first segment, try to navigate to the previous session
            const previousSession = findPreviousSession();
            if (previousSession) {
                const prevSessionStatusData = contentStatus[previousSession.session_code];
                const prevSessionStatus = typeof prevSessionStatusData === 'object' ? prevSessionStatusData.status : prevSessionStatusData;
                if (prevSessionStatus === 'done') {
                    // Expand the session in the sidebar
                    expandSessionInSidebar(previousSession);
                    // Load the previous session starting at the last segment
                    fetchSessionSlides(previousSession, true);
                }
            } else {
                console.log('No previous session available or previous session is not ready');
            }
        }
    };

    // Helper function to find the next session
    const findNextSession = useCallback(() => {
        if (!selectedSession || !modulesData || modulesData.length === 0) {
            return null;
        }

        const currentSessionCode = selectedSession.session_code;
        let foundCurrent = false;
        let nextSession = null;

        // Iterate through modules, units, and sessions to find the next one
        for (const module of modulesData) {
            if (!module.units) continue;
            for (const unit of module.units) {
                if (!unit.lessons) continue;
                for (const session of unit.lessons) {
                    if (foundCurrent) {
                        // This is the next session
                        nextSession = session;
                        break;
                    }
                    if (session.session_code === currentSessionCode) {
                        foundCurrent = true;
                    }
                }
                if (nextSession) break;
            }
            if (nextSession) break;
        }

        return nextSession;
    }, [selectedSession, modulesData]);

    // Helper function to find the previous session
    const findPreviousSession = useCallback(() => {
        if (!selectedSession || !modulesData || modulesData.length === 0) {
            return null;
        }

        const currentSessionCode = selectedSession.session_code;
        let previousSession = null;

        // Iterate through modules, units, and sessions to find the previous one
        for (const module of modulesData) {
            if (!module.units) continue;
            for (const unit of module.units) {
                if (!unit.lessons) continue;
                for (const session of unit.lessons) {
                    if (session.session_code === currentSessionCode) {
                        // Return the previous session we stored
                        return previousSession;
                    }
                    // Store this session as a potential previous session
                    previousSession = session;
                }
            }
        }

        return null;
    }, [selectedSession, modulesData]);

    // Helper function to find module and unit indices for a session and expand them
    const expandSessionInSidebar = useCallback((session) => {
        if (!session || !modulesData || modulesData.length === 0) return;

        let moduleIndex = -1;
        let unitIndex = -1;

        // Find the module and unit indices for this session
        for (let mIdx = 0; mIdx < modulesData.length; mIdx++) {
            const module = modulesData[mIdx];
            if (!module.units) continue;

            for (let uIdx = 0; uIdx < module.units.length; uIdx++) {
                const unit = module.units[uIdx];
                if (!unit.lessons) continue;

                const foundSession = unit.lessons.find(s => s.session_code === session.session_code);
                if (foundSession) {
                    moduleIndex = mIdx;
                    unitIndex = uIdx;
                    break;
                }
            }
            if (moduleIndex !== -1) break;
        }

        // Expand the module and unit
        if (moduleIndex !== -1) {
            setOpenModules(prev => ({
                ...prev,
                [moduleIndex]: true
            }));
        }

        if (moduleIndex !== -1 && unitIndex !== -1) {
            setOpenUnits(prev => ({
                ...prev,
                [`${moduleIndex}-${unitIndex}`]: true
            }));
        }

        // Expand the session
        const sessionCode = session.session_code;
        const newOpenSessions = {};
        Object.keys(openSessions).forEach(key => { newOpenSessions[key] = false; });
        newOpenSessions[sessionCode] = true;
        setOpenSessions(newOpenSessions);

        // On mobile, expand the sidebar if it's collapsed to show the new session
        if (isMobile && isCollapsed) {
            setIsCollapsed(false);
        }
    }, [modulesData, openSessions, isMobile, isCollapsed]);

    const handleNextSegment = () => {
        if (segments.length === 0) return;

        const currentIndex = segments.findIndex(seg => seg.id === activeSegment);

        // Check if we can move to the next segment within current session
        if (currentIndex < segments.length - 1) {
            const nextSegment = segments[currentIndex + 1];
            setActiveSegment(nextSegment.id);
            setCurrentSlide(0); // Go to first slide of next segment
            setCurrentSegment(currentIndex + 1);
        } else if (currentIndex === segments.length - 1) {
            // We're at the last segment, try to navigate to the next session
            const nextSession = findNextSession();
            if (nextSession) {
                const nextSessionStatusData = contentStatus[nextSession.session_code];
                const nextSessionStatus = typeof nextSessionStatusData === 'object' ? nextSessionStatusData.status : nextSessionStatusData;
                if (nextSessionStatus === 'done') {
                    // Expand the session in the sidebar
                    expandSessionInSidebar(nextSession);
                    // Load the next session
                    fetchSessionSlides(nextSession);
                }
            } else {
                console.log('No next session available or next session is not ready');
            }
        }
    };

    useEffect(() => {
        if (dqcoList.length === 0) fetchV3Level7DQCodeList();
        // eslint-disable-next-line
    }, []);

    const fetchV3Level7DQCodeList = async () => {
        try {
            const res = await listV3Level7DQCodeService();
            if (res?.data?.metaData) {
                setDqcoList(res.data.metaData);
            }
        } catch (err) {
            // Optionally handle error
            setDqcoList([]);
        }
    };

    const handleModuleCodeClick = async (module, dqco) => {
        // If dqcoList is empty, fetch it
        if (dqcoList.length === 0) {
            await fetchV3Level7DQCodeList();
        }

        const dqcoObj = dqcoList.find(item => item.dqco_no === dqco);

        setPopupContent({
            code: dqco,
            dqc_no: dqcoObj?.dqc_no,
            title: dqcoObj?.full_name,
            description: dqcoObj?.definition
        });
    };

    const handleLaunchProgram = () => {
        setDialogOpen(false);
        if (segments.length > 0) {
            setActiveSegment(segments[0].id);
            setCurrentSegment(0);
            setCurrentSlide(0);
        }
    };

    const handleDialogClose = () => {
        setDialogOpen(false);
    };

    const isSegmentActive = (segmentId) => {
        return activeSegment === segmentId;
    }

    // Helper to check if there's a next session available
    const hasNextSession = useMemo(() => {
        const nextSession = findNextSession();
        if (!nextSession) return false;
        const nextSessionStatusData = contentStatus[nextSession.session_code];
        const nextSessionStatus = typeof nextSessionStatusData === 'object' ? nextSessionStatusData.status : nextSessionStatusData;
        return nextSessionStatus === 'done';
    }, [findNextSession, contentStatus]);

    // Helper to check if there's a previous session available
    const hasPreviousSession = useMemo(() => {
        const previousSession = findPreviousSession();
        if (!previousSession) return false;
        const prevSessionStatusData = contentStatus[previousSession.session_code];
        const prevSessionStatus = typeof prevSessionStatusData === 'object' ? prevSessionStatusData.status : prevSessionStatusData;
        return prevSessionStatus === 'done';
    }, [findPreviousSession, contentStatus]);

    // Determine if we should show next/previous segment buttons on slides
    const showNextSegmentButton = currentSlide === currentSegmentSlides.length - 1 && (currentSegment < segments.length - 1 || hasNextSession);
    const showPrevSegmentButton = currentSlide === 0 && (currentSegment > 0 || hasPreviousSession);

    // Determine if we're on the last segment and should show "View Next Session"
    const isLastSegmentWithNextSession = currentSegment === segments.length - 1 && hasNextSession;

    // Helper to get assessment items for the active session/segment
    const getActiveAssessmentItems = () => {
        const sessKey = selectedSession?.session_code;
        const assess = sessionAssessments?.[sessKey] || {};
        // console.log('getActiveAssessmentItems called:', {
        //     sessKey,
        //     activeSegment,
        //     assessData: assess,
        //     pre: assess.pre,
        //     post: assess.post
        // });
        if (activeSegment === '__PRE_ASSESSMENT__') {
            // console.log('Returning PRE assessment items:', assess.pre);
            return assess.pre || [];
        }
        if (activeSegment === '__POST_ASSESSMENT__') {
            // console.log('Returning POST assessment items:', assess.post);
            return assess.post || [];
        }
        return [];
    };

    // Handler to open assessment visibility modal
    const handleOpenAssessmentModal = (assessmentType, currentVisibility) => {
        setAssessmentModalType(assessmentType); // 'pre' or 'post'
        setAssessmentModalAction(currentVisibility ? 'hide' : 'unhide');
        setAssessmentModalOpen(true);
    };

    // Handler to close assessment visibility modal
    const handleCloseAssessmentModal = () => {
        setAssessmentModalOpen(false);
        setAssessmentModalType(null);
        setAssessmentModalAction(null);
    };

    // Handler to confirm assessment visibility change
    const handleConfirmAssessmentVisibility = async () => {
        if (!selectedSession || !assessmentModalType || !programData?.code) return;

        try {
            const sessionCode = selectedSession.session_code;
            const newValue = assessmentModalAction === 'unhide';

            // Update BOTH pre and post assessments to the same value
            const updatedVisibility = {
                show_pre_assessment: newValue,
                show_post_assessment: newValue
            };

            // Make API call to update assessment visibility
            await updateAssessmentVisibilityLearningProgramService(
                programData.code,
                sessionCode,
                updatedVisibility.show_pre_assessment,
                updatedVisibility.show_post_assessment
            );

            // Update local state on success
            setAssessmentVisibility(prev => ({
                ...prev,
                [sessionCode]: updatedVisibility
            }));

            console.log(`Updated both assessments for session ${sessionCode} to ${newValue}`);

            handleCloseAssessmentModal();
        } catch (error) {
            console.error('Error updating assessment visibility:', error);
            // You may want to show an error toast here
        }
    };

    return (
        <Box sx={{
            mt:{xs:2,sm:'75px', lg:2},
            overflowY: 'hidden',
            flexDirection: {xs: 'column', sm: 'row'},
            display: 'flex',
            width: '100%',
            minHeight: '82vh'
        }}>
            {/* Left Container - Curriculum List */}
            <Box className={classes.leftContainer} style={{
                height: isMobile ? (isCollapsed ? "100px" : "82vh") : "82vh",
                width: !isMobile ? (isCollapsed ? "48px" : "32%") : "100%",
                marginRight: !isMobile ? (isCollapsed ? "20px" : "20px") : "auto",
                padding: !isMobile ? (isCollapsed ? "6px 4px" : "10px") : "auto",
                borderRadius: isCollapsed ? "8px" : "8px",
                alignItems: isCollapsed && !isMobile ? 'center' : undefined
            }}>
                {isCollapsed ? (
                    isMobile ? (
                        <Box
                            style={{
                                width: "100%",
                                padding: "8px 0",
                                display: "flex",
                                flexDirection: "row",
                                alignItems: "center",
                                justifyContent: "space-between",
                            }}
                        >
                            <IconButton
                                onClick={() => history.push(`/dq_pro/products/program/dashboard/${dq_module_code}`)}
                                disableRipple
                                disableFocusRipple
                                style={{
                                    color: '#0097B2',
                                    backgroundColor: '#ffffff',
                                    border: '1px solid #e6e9ef',
                                    boxShadow: '0 2px 6px rgba(0,0,0,0.06)',
                                    cursor: 'pointer',
                                    outline: "none",
                                }}
                            >
                                <ArrowBack style={{fontSize: 22}}/>
                            </IconButton>

                            {/* Title on top (horizontal) */}
                            <Typography variant="h4" className={classes.title} style={{textAlign: "center",}}>
                                {programData?.full_name}
                            </Typography>

                            {/* Expand button below title */}
                            <IconButton
                                onClick={toggleCollapse}
                                style={{
                                    marginTop: "6px",
                                    color: "#ef6528",
                                    backgroundColor: "#fff",
                                    border: "1px solid #e6e9ef",
                                    boxShadow: "0 2px 6px rgba(0,0,0,0.06)",
                                }}
                            >
                                <ExpandMoreOutlined style={{fontSize: 22}}/>
                            </IconButton>
                        </Box>
                    ) : (
                        <>
                            {/* Expand button at top */}
                            <IconButton
                                onClick={toggleCollapse}
                                style={{
                                    position: "absolute",
                                    top: 10,
                                    left: "50%",
                                    transform: "translateX(-50%)",
                                    color: "#ef6528",
                                    backgroundColor: "#fff",
                                    border: "1px solid #e6e9ef",
                                    boxShadow: "0 2px 6px rgba(0,0,0,0.06)",
                                    zIndex: 2,
                                    padding: 6,
                                }}
                            >
                                <ChevronRight style={{fontSize: 22}}/>
                            </IconButton>

                            {/* Vertical program title in center */}
                            <Box
                                style={{
                                    flexGrow: 1,
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    fontWeight: "bold",
                                    color: "#0097B2",
                                    padding: "0 10px",
                                    cursor: "default"
                                }}
                            >
                                <Typography variant="h4" className={classes.title} style={{
                                    writingMode: "vertical-rl",
                                    transform: "rotate(180deg)",
                                    textAlign: "center",
                                }}>
                                    {programData?.full_name}
                                </Typography>
                            </Box>
                        </>
                    )
                ) : (
                    <Box className={classes.leftContentContainer}>
                        {loading ? (
                            <Typography>Loading...</Typography>
                        ) : (
                            <>
                                {/*icon button to go back to program dashboard page*/}
                                {!isCollapsed && (
                                    <Box sx={{
                                        position: 'absolute',
                                        top: 0,
                                        left: 0,
                                        right: 0,
                                        height: '75px',
                                        mb: 4,
                                        borderRadius: '8px',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'space-between',
                                        padding: '0 8px',
                                        backgroundColor: '#ffff',
                                        zIndex: 100,
                                    }}>
                                        <IconButton
                                            onClick={() => history.push(`/dq_pro/products/program/dashboard/${dq_module_code}`)}
                                            disableRipple
                                            disableFocusRipple
                                            style={{
                                                color: '#0097B2',
                                                backgroundColor: '#ffffff',
                                                border: '1px solid #e6e9ef',
                                                boxShadow: '0 2px 6px rgba(0,0,0,0.06)',
                                                cursor: 'pointer',
                                                outline: "none",
                                            }}
                                        >
                                            <ArrowBack style={{fontSize: 22}}/>
                                        </IconButton>

                                        <Typography variant="h4" className={classes.title}>
                                            {programData?.full_name}
                                        </Typography>
                                        {/*icon button to collapse course catalogue*/}
                                        <IconButton
                                            onClick={toggleCollapse}
                                            disableRipple
                                            disableFocusRipple
                                            style={{
                                                transition: 'all 0.3s ease',
                                                zIndex: 99,
                                                cursor: 'pointer',
                                                outline: "none",
                                                color: '#ef6528',
                                                backgroundColor: '#ffffff',
                                                border: '1px solid #e6e9ef',
                                                boxShadow: '0 2px 6px rgba(0,0,0,0.06)'
                                            }}
                                        >
                                            {isMobile ? (
                                                <ExpandLessOutlined style={{fontSize: 22}}/>
                                            ) : (
                                                <ChevronLeft style={{fontSize: 22}}/>
                                            )}
                                        </IconButton>
                                    </Box>
                                )}

                                {modulesData.map((module, moduleIndex) => (
                                    <Card className={classes.card} key={module.module_code}>
                                        <CardContent className={classes.cardContent}>
                                            <Box
                                                onClick={() => handleToggleModuleDropdown(moduleIndex)}
                                                style={{cursor: 'pointer'}}
                                            >
                                                <Box display="flex" justifyContent="space-between" alignItems="center">
                                                    <Typography variant="h6" style={{
                                                        fontSize: '1.2rem',
                                                        color: '#ED6628',
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        gap: '8px'
                                                    }}>
                                                        {`Module ${moduleIndex + 1}: ${module.module_name}`}
                                                    </Typography>
                                                    {openModules[moduleIndex] ? (
                                                        <ArrowDropUp style={{color: '#D35400'}}/>
                                                    ) : (
                                                        <ArrowDropDown style={{color: '#D35400'}}/>
                                                    )}
                                                </Box>

                                                <Box mt={1} display="flex" flexWrap="wrap" gap={1}>
                                                    {module.unique_dqco?.map((dqco, idx) => {
                                                        const dqcoObj = dqcoList.find(item => item.dqco_no === dqco);
                                                        return (
                                                            <span
                                                                key={dqco || idx}
                                                                style={{
                                                                    backgroundColor: DQC_COLORS[dqcoObj?.dqc_no] || '#D2DD2C',
                                                                    color: '#ffffff',
                                                                    borderRadius: '20px',
                                                                    fontSize: '12px',
                                                                    padding: '1px 7px',
                                                                    fontWeight: 500,
                                                                    cursor: 'pointer',
                                                                    margin: '4px',
                                                                }}
                                                                onClick={e => {
                                                                    e.stopPropagation();
                                                                    handleModuleCodeClick(module, dqco);
                                                                }}
                                                            >
                                                        {dqco}
                                                    </span>
                                                        );
                                                    })}
                                                </Box>
                                            </Box>
                                            {popupContent && (
                                                <Box
                                                    style={{
                                                        position: 'fixed',
                                                        top: '50%',
                                                        left: '50%',
                                                        transform: 'translate(-50%, -50%)',
                                                        width: '80%',
                                                        maxWidth: '500px',
                                                        backgroundColor: '#fff',
                                                        border: '1px solid #000',
                                                        borderRadius: '20px',
                                                        boxShadow: 24,
                                                        padding: 4,
                                                        zIndex: 1300,
                                                    }}
                                                    onClick={() => setPopupContent(null)}
                                                >
                                                    <IconButton
                                                        aria-label="close"
                                                        onClick={() => setPopupContent(null)}
                                                        style={{
                                                            position: 'absolute',
                                                            top: 8,
                                                            right: 0,
                                                            color: '#000',
                                                        }}
                                                    >
                                                        <Close/>
                                                    </IconButton>
                                                    <Box sx={{
                                                        display: 'flex',
                                                        flexDirection: 'column',
                                                        alignItems: 'center',
                                                        gap: 2, padding: '15px 20px',
                                                    }}>
                                                        <Box sx={{
                                                            backgroundColor: DQC_COLORS[popupContent.dqc_no] || '#D2DD2C',
                                                            color: 'white',
                                                            borderRadius: '20px',
                                                            px: 2,
                                                            py: 1,
                                                            fontWeight: 'medium',
                                                            mb: 2
                                                        }}>
                                                            {popupContent.code}
                                                        </Box>

                                                        {/* Title */}
                                                        <Typography variant="h5" component="h2" style={{
                                                            fontWeight: 'bold',
                                                            textAlign: 'center',
                                                            color: DQC_COLORS[popupContent.dqc_no] || '#D2DD2C',
                                                        }}>
                                                            {popupContent.title}
                                                        </Typography>

                                                        {/* Description */}
                                                        <Typography style={{
                                                            mt: 2,
                                                            whiteSpace: 'pre-line',
                                                            textAlign: 'left',
                                                            fontSize: '11px',
                                                            color: '#000'
                                                        }}>
                                                            {popupContent.description}
                                                        </Typography>
                                                    </Box>
                                                </Box>
                                            )}
                                            {openModules[moduleIndex] && (
                                                <>
                                                    {module.units.map((unit, unitIndex) => (
                                                        <Card
                                                            style={{boxShadow: 'none', backgroundColor: 'transparent'}}
                                                            key={unit.unit_code}>
                                                            <CardContent>
                                                                <Box
                                                                    display="flex"
                                                                    alignItems="center"
                                                                    justifyContent="space-between"
                                                                    onClick={() => handleToggleUnitDropdown(moduleIndex, unitIndex)}
                                                                    style={{cursor: 'pointer'}}
                                                                >
                                                                    <Typography variant="h6" style={{color: '#e7456d'}}>
                                                                        {`Unit ${unitIndex + 1}: ${unit.unit_title}`}
                                                                    </Typography>
                                                                    {openUnits[`${moduleIndex}-${unitIndex}`] ? (
                                                                        <ArrowDropUp style={{color: '#ED6628'}}/>
                                                                    ) : (
                                                                        <ArrowDropDown style={{color: '#ED6628'}}/>
                                                                    )}
                                                                </Box>

                                                                {openUnits[`${moduleIndex}-${unitIndex}`] && (
                                                                    <>
                                                                        <Divider style={{
                                                                            height: '4px',
                                                                            marginTop: '10px',
                                                                            backgroundColor: '#F17632',
                                                                            borderRadius: '15px',
                                                                        }}/>
                                                                        {unit.lessons.map((session, sessionIndex) => (
                                                                            <Box key={session.session_code}>
                                                                                <Box
                                                                                    className={classes.lessonContainer}
                                                                                    style={{
                                                                                        backgroundColor: selectedSession?.session_code === session.session_code
                                                                                            ? 'rgba(238, 102, 40, 0.15)'
                                                                                            : 'transparent',
                                                                                        borderRadius: selectedSession?.session_code === session.session_code
                                                                                            ? '8px'
                                                                                            : '0px',
                                                                                        cursor: (() => {
                                                                                            const statusData = contentStatus[session.session_code];
                                                                                            const status = typeof statusData === 'object' ? statusData.status : statusData;
                                                                                            return status === 'done' ? 'pointer' : 'default';
                                                                                        })(),
                                                                                        opacity: (() => {
                                                                                            const statusData = contentStatus[session.session_code];
                                                                                            const status = typeof statusData === 'object' ? statusData.status : statusData;
                                                                                            return status === 'failed' ? 0.6 : 1;
                                                                                        })()
                                                                                    }}
                                                                                    onClick={() => {
                                                                                        const statusData = contentStatus[session.session_code];
                                                                                        const status = typeof statusData === 'object' ? statusData.status : statusData;
                                                                                        if (status === 'done') handleToggleSessionDropdown(session);
                                                                                    }}
                                                                                >
                                                                                    <Box className={classes.sectionRow}>
                                                                                        <Typography
                                                                                            variant="h4"
                                                                                            className={classes.sectionText}
                                                                                            style={{
                                                                                                color: (() => {
                                                                                                    const statusData = contentStatus[session.session_code];
                                                                                                    const status = typeof statusData === 'object' ? statusData.status : statusData;
                                                                                                    return status === 'failed' ? '#999' : '#5A5A5A';
                                                                                                })()
                                                                                            }}
                                                                                        >
                                                                                            {`Session ${sessionIndex + 1}: ${session.lesson_title}`}
                                                                                        </Typography>

                                                                                        {/* Status indicators based on contentStatus */}
                                                                                        {(() => {
                                                                                            const statusData = contentStatus[session.session_code];
                                                                                            const status = typeof statusData === 'object' ? statusData.status : statusData;

                                                                                            if (status === 'in_progress') {
                                                                                                return <CircularProgress size={20} style={{color: '#ED6628'}}/>;
                                                                                            } else if (status === 'failed') {
                                                                                                return <CancelIcon style={{color: '#FF5757'}}/>;
                                                                                            } else if (openSessions[session.session_code]) {
                                                                                                return <ArrowDropUp style={{color: '#ED6628'}}/>;
                                                                                            } else {
                                                                                                return <ArrowDropDown style={{color: '#ED6628'}}/>;
                                                                                            }
                                                                                        })()}
                                                                                    </Box>
                                                                                </Box>

                                                                                {/* Session segments in the side menu */}
                                                                                {openSessions[session.session_code] && (
                                                                                    <Box style={{marginTop: '8px'}}>
                                                                                        {/* Show loader while segments are loading */}
                                                                                        {slidesLoading && selectedSession?.session_code === session.session_code ? (
                                                                                            <Box style={{
                                                                                                display: 'flex',
                                                                                                alignItems: 'center',
                                                                                                justifyContent: 'center',
                                                                                                padding: '15px'
                                                                                            }}>
                                                                                                <CircularProgress
                                                                                                    size={20}
                                                                                                    style={{color: '#ED6628'}}/>
                                                                                                <Typography
                                                                                                    variant="body2"
                                                                                                    style={{
                                                                                                        marginLeft: '10px',
                                                                                                        color: '#555'
                                                                                                    }}>
                                                                                                    Loading segments...
                                                                                                </Typography>
                                                                                            </Box>
                                                                                        ) : (
                                                                                            segments.map((segment, segIdx) => {
                                                                                                const isActive = isSegmentActive(segment.id) && selectedSession?.session_code === session.session_code;

                                                                                                // Check if this is a Pre/Post Assessment segment
                                                                                                const isAssessmentSegment = segment.id === '__PRE_ASSESSMENT__' || segment.id === '__POST_ASSESSMENT__';
                                                                                                const assessmentType = segment.id === '__PRE_ASSESSMENT__' ? 'pre' : 'post';
                                                                                                const sessionVisibility = assessmentVisibility[session.session_code] || {};
                                                                                                const isVisible = segment.id === '__PRE_ASSESSMENT__'
                                                                                                    ? (sessionVisibility.show_pre_assessment !== false)
                                                                                                    : (sessionVisibility.show_post_assessment !== false);

                                                                                                return (
                                                                                                    <Box
                                                                                                        className={classes.lessonContainer}
                                                                                                        key={segment.id || segIdx}
                                                                                                        style={{
                                                                                                            backgroundColor: isActive ? 'rgba(238, 102, 40, 0.1)' : 'transparent',
                                                                                                            borderRadius: isActive ? '8px' : '0',
                                                                                                            padding: '8px 10px 8px 20px',
                                                                                                            marginBottom: '4px'
                                                                                                        }}
                                                                                                    >
                                                                                                        <Box
                                                                                                            className={classes.sectionSegmentRow}>
                                                                                                            <Box style={{ display: 'flex', alignItems: 'center', gap: '8px', flex: 1 }}>
                                                                                                                <Typography
                                                                                                                    variant="h6"
                                                                                                                    className={classes.sectionText}
                                                                                                                    style={{
                                                                                                                        color: isActive ? '#ED6628' : '#000000',
                                                                                                                        fontWeight: isActive ? 600 : 400
                                                                                                                    }}
                                                                                                                >
                                                                                                                    {segment.name}
                                                                                                                </Typography>
                                                                                                                {isAssessmentSegment && (
                                                                                                                    <Button
                                                                                                                        variant="text"
                                                                                                                        size="small"
                                                                                                                        onClick={(e) => {
                                                                                                                            e.stopPropagation();
                                                                                                                            handleOpenAssessmentModal(assessmentType, isVisible);
                                                                                                                        }}
                                                                                                                        style={{
                                                                                                                            minWidth: 'auto',
                                                                                                                            padding: '2px 8px',
                                                                                                                            fontSize: '11px',
                                                                                                                            textTransform: 'none',
                                                                                                                            color: '#fff',
                                                                                                                            fontWeight: 500,
                                                                                                                            background: '#ff6468',
                                                                                                                        }}
                                                                                                                    >
                                                                                                                        {isVisible ? 'Hide' : 'Unhide'}
                                                                                                                    </Button>
                                                                                                                )}
                                                                                                            </Box>
                                                                                                            <Button
                                                                                                                variant="contained"
                                                                                                                className={classes.viewButton}
                                                                                                                disabled={slidesLoading}
                                                                                                                onClick={() => {
                                                                                                                    handleViewSegment(session, segment.id);
                                                                                                                    if (isMobile) {
                                                                                                                        toggleCollapse();
                                                                                                                    }
                                                                                                                }}
                                                                                                                style={{
                                                                                                                    backgroundColor: isActive ? '#ED6628' : '#0097B2',
                                                                                                                    color: '#fff'
                                                                                                                }}
                                                                                                            >
                                                                                                                View
                                                                                                            </Button>
                                                                                                        </Box>
                                                                                                    </Box>
                                                                                                );
                                                                                            })
                                                                                        )}
                                                                                    </Box>
                                                                                )}
                                                                            </Box>
                                                                        ))}
                                                                    </>
                                                                )}
                                                            </CardContent>
                                                        </Card>
                                                    ))}
                                                </>
                                            )}
                                        </CardContent>
                                    </Card>
                                ))}
                                <IconButton
                                    style={{
                                        display: 'none',
                                        position: 'absolute',
                                        top: '115px',
                                        left: '50%',
                                        fontSize: '30px',
                                        transform: 'translateX(-50%)',
                                        color: '#F17632',
                                    }}
                                >
                                    {isCollapsed ? (
                                        <ArrowDropDown style={{color: '#ED6628'}}/>
                                    ) : (
                                        <ArrowDropUp style={{color: '#ED6628'}}/>
                                    )}
                                </IconButton>
                            </>
                        )}

                    </Box>
                )}
            </Box>

            {/* Right Container - Slideshow or Initial Loading State */}
            {initialLoading ? (
                <Box className={classes.rightContainer} style={{
                    display: 'flex',
                    height: '82vh',
                    overflow: 'scroll',
                    overflowX: 'hidden',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center'
                }}>
                    <CircularProgress size={60} style={{color: '#ED6628', marginBottom: '20px'}}/>
                    <Typography variant="h5" style={{color: '#555', textAlign: 'center', maxWidth: '600px'}}>
                        Loading content... This might take a while as we prepare the learning materials.
                    </Typography>
                    <Typography variant="body1"
                                style={{color: '#777', textAlign: 'center', maxWidth: '600px', marginTop: '20px'}}>
                        Please be patient. The first load may take longer than usual, especially if content is being
                        generated.
                    </Typography>
                </Box>
            ) : (selectedSession && selectedSection && activeSegment) ? (
                <Box className={classes.rightContainer}
                     style={{
                         width: !isMobile ? (isCollapsed ? "100%" : "68%") : "100%",
                         maxWidth: '100%',
                         overflowX: 'hidden'
                     }}>
                    {/* Slides Content */}
                    <Box className={classes.rightContentContainer}>
                        <Typography variant="h4" style={{
                            color: '#ED6628',
                            fontWeight: 'bold',
                            textAlign: 'center',
                            marginBottom: '40px !important',
                            marginTop: '10px',
                            fontSize: 'clamp(30px, 3.2vw, 35px)'
                        }}>
                            {selectedSession?.lesson_title}
                        </Typography>
                        {(activeSegment === '__PRE_ASSESSMENT__' || activeSegment === '__POST_ASSESSMENT__') && (
                            <Typography variant="h5" style={{
                                color: '#fe696e',
                                fontWeight: 'bold',
                                textAlign: 'center',
                                marginBottom: '40px !important',
                                marginTop: '10px',
                                fontSize: 'clamp(22px, 3.2vw, 28px)'
                            }}>
                                {activeSegment === '__PRE_ASSESSMENT__' ? 'Pre Assessment' : 'Post Assessment'}
                            </Typography>
                        )}
                        <Box style={{
                            display: 'flex',
                            flexDirection: 'row',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            width: isMobile ? '85%' : isCollapsed ? '80%' : '88%',
                            marginTop: '13px',
                            gap: '12px',
                            flexWrap: 'nowrap',
                        }}>
                            <Box style={{flex: '1 1 auto', minWidth: 0, paddingRight: 28}}>
                                <AirbnbSlider
                                    value={currentSlide}
                                    min={0}
                                    max={currentSegmentSlides.length > 0 ? currentSegmentSlides.length - 1 : 0}
                                    step={1}
                                    slots={{thumb: RazThumbComponent}}
                                    disabled
                                    style={{width: '100%', minWidth: 0}}
                                />
                            </Box>

                            <Box sx={{
                                display: 'flex',
                                justifyContent: 'flex-end',
                                flex: '0 0 auto',
                                width: 'auto',
                                minWidth: 'fit-content',
                                whiteSpace: 'nowrap',
                                marginTop: '4px',
                            }}>
                                <Typography style={{
                                    color: '#777',
                                    fontWeight: '700',
                                    fontSize: 'clamp(16px, 2.4vw, 22px)',
                                    paddingLeft: 4
                                }}>
                                    <span
                                        style={{color: '#ED6628'}}>{currentSlide + 1} </span>/ {currentSegmentSlides.length}
                                </Typography>
                            </Box>
                        </Box>

                        <Box className={classes.slidesContainer} sx={{
                            padding: {xs: '0px', sm: '0 20px 20px 20px'},
                            width: {xs: '90vw', sm: '100%'},
                            paddingLeft: {xs: '30px', sm: '72px', md: '0px',},
                            paddingRight: {xs: '30px', sm: '72px', md: '0px',},
                        }}>
                            {slidesLoading ? (
                                <Box style={{
                                    display: 'flex',
                                    flexDirection: 'column',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    height: '300px',
                                    width: '100%'
                                }}>
                                    <CircularProgress size={40} style={{color: '#ED6628', marginBottom: '20px'}}/>
                                    <Typography variant="body1" style={{color: '#555'}}>
                                        Loading slides...
                                    </Typography>
                                </Box>
                            ) : (activeSegment === '__PRE_ASSESSMENT__' || activeSegment === '__POST_ASSESSMENT__') ? (
                                <SessionAssessmentViewer
                                    key={activeSegment} // Force re-mount when switching between pre/post
                                    title={activeSegment === '__PRE_ASSESSMENT__' ? 'Pre Assessment' : 'Post Assessment'}
                                    items={getActiveAssessmentItems()}
                                    currentQuestionIndex={currentSlide}
                                    highlightWord={highlightWordRenderer}
                                />
                            ) : currentSegmentSlides.length > 0 && currentSegmentSlides[currentSlide] ? (() => {
                                const sessionSlide = currentSegmentSlides[currentSlide];
                                const isV2SupportedLayout = [
                                    'TemplateGraphicAndText',
                                    'TemplateList',
                                    'TemplateLongText',
                                    'TemplateStepBlock',
                                    'TemplateSessionStart',
                                    'TemplateHtmlContent',
                                    'PledgeTemplate',
                                    'VideoTemplate',
                                    'JOURNALING_SELF_REFLECTION_ACTIVITY',
                                    'SHARE_WORK_FILE_ACTIVITY',
                                    'POLL_QUIZ_ACTIVITY',
                                    'DISCUSSION_ACTIVITY',
                                    'ASSESSMENT_RESULT',
                                    'TEMPLATE_WITH_SUBTITLE',
                                ].includes(sessionSlide.SlideLayout);

                                if (isV2SupportedLayout) {
                                    return (
                                        <SlideComponentV2
                                            slide={sessionSlide}
                                            highlightWord={highlightWordRenderer}
                                            skipTitleSpeech={skipTitleSpeech}
                                        />
                                    );
                                } else {
                                    const SlideComponent = PBDQ3_PROGRAM_SESSION_CONTENT_LAYOUT_VERBOSE[sessionSlide.SlideLayout];

                                    if (!SlideComponent) {
                                        console.error(`No component found for layout: ${sessionSlide.SlideLayout}`);
                                        return (
                                            <Typography variant="h6" color="error">
                                                Error: Slide layout not found
                                            </Typography>
                                        );
                                    }

                                    return (
                                        <SlideComponent
                                            title={sessionSlide.SlideTitle}
                                            slideContent={Array.isArray(sessionSlide.SlideContent) ? sessionSlide.SlideContent : [sessionSlide.SlideContent]}
                                            images={sessionSlide.Images || []}
                                        />
                                    );
                                }
                            })() : (
                                <Typography variant="h6" style={{color: '#555'}}>
                                    No slides available for this segment
                                </Typography>
                            )}
                        </Box>

                        {/* Navigation Buttons Below Slides */}
                        <Box sx={{
                            flex: '0 0 auto',
                            borderTop: '1px solid #eee',
                            display: 'flex',
                            flexDirection: 'row',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            width: '100%',
                            height: '13%',
                            margin: '20px 0 0 0',
                            padding: '10px 0 0 0',
                            gap: {xs: '20px', sm: '0'},
                        }}>
                            <Button
                                variant="outlined"
                                sx={{
                                    fontSize: {xs: '12px', sm: 'auto',},
                                    border: '1px solid #0097B2',
                                    color: '#0097B2',
                                    backgroundColor: '#fff',
                                    padding: {xs:'1px 8px',sm:'4px 12px', lg:'8px 20px'},
                                    borderRadius: '20px',
                                    opacity: (currentSegment === 0 && !hasPreviousSession) ? 0.5 : 1,
                                    minWidth: {xs:'80px', sm:'100px', lg:'220px'},
                                }}
                                onClick={handlePreviousSegment}
                                disabled={currentSegment === 0 && !hasPreviousSession}
                            >
                                View Previous Segment
                            </Button>

                            {/* TTS Controls */}
                            <div
                                style={{
                                    position: 'relative',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'space-between',
                                    gap: 12,
                                    padding: '8px 5px',
                                    borderRadius: '20px',
                                    background: '#ED6628',
                                    boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
                                    backdropFilter: 'blur(6px)',
                                    height: '43px',
                                    width: '180px',
                                }}
                            >
                                <IconButton
                                    disabled={!speaking}
                                    style={{
                                        color: speaking ? '#fff' : '#DDDDDD',
                                        cursor: speaking ? 'pointer' : 'not-allowed',
                                        width: '40px',
                                        height: '40px',
                                        fontSize: 28,
                                        border: 'none',
                                        boxShadow: '0 4px 12px rgba(252,240,235,0.25)',
                                        outline: 'none',
                                    }}
                                    sx={{
                                        '&:focus': { outline: 'none' },
                                        '&:focus-visible': { outline: 'none' },
                                    }}
                                    onClick={() => {
                                        if (!speaking) {
                                            setTtsAuto(true);
                                            startSpeech();
                                        } else {
                                            setTtsAuto(false);
                                            stopSpeech();
                                        }
                                    }}

                                >
                                    <Stop fontSize="inherit" />
                                </IconButton>
                                <IconButton
                                    style={{
                                        backgroundColor: '#fff',
                                        color:  '#ED6628',
                                        width: 60,
                                        height: 60,
                                        borderRadius: '50%',
                                        boxShadow: '0 1px 20px rgba(0,0,0,0.25)',
                                        position: 'absolute',
                                        top: '-8px',
                                        left: '50%',
                                        transform: 'translateX(-50%)',
                                        zIndex: 20,
                                        transition: 'all 0.25s ease',
                                        fontSize: 52,
                                        outline: 'none',
                                    }}
                                    sx={{
                                        '&:focus': { outline: 'none' },
                                        '&:focus-visible': { outline: 'none' },
                                    }}
                                    onClick={() => {
                                    if (!speaking) {
                                        setTtsAuto(true);
                                        startSpeech();
                                    } else {
                                        pauseSpeech();
                                    }
                                  }}
                                >
                                    {speaking && !paused ? (
                                        <Pause fontSize="inherit"  />
                                    ) : (
                                        <PlayArrow fontSize="inherit" />
                                    )}
                                </IconButton>

                                {/* Settings toggle */}
                                <IconButton
                                    aria-label="TTS settings"
                                    onClick={(e) => setTtsSettingsAnchor(e.currentTarget)}
                                    style={{
                                        color: '#fff',
                                        width: 40,
                                        height: 40,
                                        fontSize: 28,
                                        border: 'none',
                                        borderRadius: '50%',
                                        transition: 'all 0.2s ease',
                                        boxShadow: '0 4px 12px rgba(252,240,235,0.25)',
                                        outline: 'none',
                                    }}
                                    sx={{
                                        '&:focus': { outline: 'none' },
                                        '&:focus-visible': { outline: 'none' },
                                    }}
                                >
                                    <Settings fontSize="inherit" />
                                </IconButton>

                                <Popover
                                    open={Boolean(ttsSettingsAnchor)}
                                    anchorEl={ttsSettingsAnchor}
                                    onClose={() => setTtsSettingsAnchor(null)}
                                    anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
                                    transformOrigin={{ vertical: 'top', horizontal: 'center' }}
                                    PaperProps={{
                                        sx: {
                                            p: 2,
                                            minWidth: 320,
                                            maxWidth: 360,
                                            borderRadius: 3,
                                            background:
                                                'linear-gradient(135deg, rgba(255, 255, 255, 0.7) 0%, rgba(245, 245, 250, 0.65) 50%, rgba(240, 240, 255, 0.6) 100%)',
                                            backdropFilter: 'blur(16px) saturate(180%)',
                                            WebkitBackdropFilter: 'blur(16px) saturate(180%)',
                                            border: '1px solid rgba(229, 231, 235, 0.4)',
                                            boxShadow: `0 8px 30px rgba(0, 0, 0, 0.15),
                                              0 4px 12px rgba(0, 0, 0, 0.1),
                                              inset 0 1px 0 rgba(255, 255, 255, 0.6)`,
                                            zIndex: 2000,
                                        },
                                    }}
                                >
                                    <Box sx={{ position: 'relative' }}>
                                        {/* Header row */}
                                        <Box
                                            sx={{
                                                display: 'flex',
                                                justifyContent: 'space-between',
                                                alignItems: 'center',
                                                mb: 2,
                                            }}
                                        >
                                            <Typography
                                                variant="h6"
                                                style={{
                                                    fontWeight: 700,
                                                    color: '#ED6628',
                                                }}
                                            >
                                                Voice Settings
                                            </Typography>

                                            <IconButton
                                                size="small"
                                                onClick={() => setTtsSettingsAnchor(null)}
                                                style={{
                                                    fontWeight:'bold',
                                                    color: '#ED6628',
                                                    border: 'none',
                                                    '&:hover': { background: '#ED6628', color: '#fff' },
                                                }}
                                            >
                                                <Close fontSize="small" />
                                            </IconButton>
                                        </Box>

                                        {/* Voice select */}
                                        <FormControl fullWidth size="small" sx={{ mb: 2 }}>
                                            <InputLabel id="voice-select-label">Voice</InputLabel>
                                            <Select
                                                labelId="voice-select-label"
                                                label="Voice"
                                                value={selectedVoiceURI || ''}
                                                onChange={(e) => setSelectedVoiceURI(e.target.value)}
                                            >
                                                {availableVoices.length === 0 && (
                                                    <MenuItem value="" disabled>
                                                        No voices available yet
                                                    </MenuItem>
                                                )}
                                                {availableVoices.map((v) => (
                                                    <MenuItem key={v.voiceURI} value={v.voiceURI}>
                                                        {v.name} ({v.lang}){v.default ? ' • default' : ''}
                                                    </MenuItem>
                                                ))}
                                            </Select>
                                        </FormControl>

                                        {/* Sliders with orange track/thumb */}
                                        <Box sx={{ mb: 2 }}>
                                            <Typography variant="caption" sx={{ color: '#666' }}>
                                                Rate: {ttsRate.toFixed(2)}
                                            </Typography>
                                            <Slider
                                                size="small"
                                                value={ttsRate}
                                                min={0.5}
                                                max={1.75}
                                                step={0.05}
                                                onChange={(_, v) => setTtsRate(Array.isArray(v) ? v[0] : v)}
                                                valueLabelDisplay="auto"
                                                sx={{
                                                    color: 'transparent',
                                                    height: 6,
                                                    borderRadius: 4,

                                                    // track styling
                                                    '& .MuiSlider-track': {
                                                        background: 'linear-gradient(90deg, #ee6d29, #f59e0b)',
                                                        border: 'none',
                                                    },
                                                    '& .MuiSlider-rail': {
                                                        background: 'linear-gradient(90deg, #ee6d29, #f59e0b)',
                                                    },

                                                    // thumb styling
                                                    '& .MuiSlider-thumb': {
                                                        width: 16,
                                                        height: 16,
                                                        backgroundColor: '#fff',
                                                        border: '2px solid #ee6d29',
                                                        boxShadow: '0 0 2px rgba(0,0,0,0.3)',
                                                    },

                                                    // value label styling
                                                    '& .MuiSlider-valueLabel': {
                                                        background: '#ee6d29',
                                                        color: '#fff',
                                                        borderRadius: '6px',
                                                    },
                                                }}
                                            />
                                        </Box>

                                        <Box sx={{ mb: 2 }}>
                                            <Typography variant="caption" sx={{ color: '#666' }}>
                                                Pitch: {ttsPitch.toFixed(2)}
                                            </Typography>
                                            <Slider
                                                size="small"
                                                value={ttsPitch}
                                                min={0.5}
                                                max={2}
                                                step={0.05}
                                                onChange={(_, v) => setTtsPitch(Array.isArray(v) ? v[0] : v)}
                                                valueLabelDisplay="auto"
                                                sx={{
                                                    color: 'transparent', // removes default color
                                                    height: 6,
                                                    borderRadius: 4,

                                                    // track styling
                                                    '& .MuiSlider-track': {
                                                        background: 'linear-gradient(90deg, #ee6d29, #f59e0b)',
                                                        border: 'none',
                                                    },
                                                    '& .MuiSlider-rail': {
                                                        background: 'linear-gradient(90deg, #ee6d29, #f59e0b)',
                                                    },

                                                    // thumb styling
                                                    '& .MuiSlider-thumb': {
                                                        width: 16,
                                                        height: 16,
                                                        backgroundColor: '#fff',
                                                        border: '2px solid #ee6d29',
                                                        boxShadow: '0 0 2px rgba(0,0,0,0.3)',
                                                    },

                                                    // value label styling
                                                    '& .MuiSlider-valueLabel': {
                                                        background: '#ee6d29',
                                                        color: '#fff',
                                                        borderRadius: '6px',
                                                    },
                                                }}
                                            />
                                        </Box>

                                        <Box sx={{ mb: 2 }}>
                                            <Typography variant="caption" sx={{ color: '#666' }}>
                                                Volume: {ttsVolume.toFixed(2)}
                                            </Typography>
                                            <Slider
                                                size="small"
                                                value={ttsVolume}
                                                min={0}
                                                max={1}
                                                step={0.05}
                                                onChange={(_, v) => setTtsVolume(Array.isArray(v) ? v[0] : v)}
                                                valueLabelDisplay="auto"
                                                sx={{
                                                    color: 'transparent', // removes default color
                                                    height: 6,
                                                    borderRadius: 4,

                                                    // track styling
                                                    '& .MuiSlider-track': {
                                                        background: 'linear-gradient(90deg, #ee6d29, #f59e0b)',
                                                        border: 'none',
                                                    },
                                                    '& .MuiSlider-rail': {
                                                        background: 'linear-gradient(90deg, #ee6d29, #f59e0b)',
                                                    },

                                                    // thumb styling
                                                    '& .MuiSlider-thumb': {
                                                        width: 16,
                                                        height: 16,
                                                        backgroundColor: '#fff',
                                                        border: '2px solid #ee6d29',
                                                        boxShadow: '0 0 2px rgba(0,0,0,0.3)',
                                                    },

                                                    // value label styling
                                                    '& .MuiSlider-valueLabel': {
                                                        background: '#ee6d29',
                                                        color: '#fff',
                                                        borderRadius: '6px',
                                                    },
                                                }}
                                            />
                                        </Box>

                                        {/* Buttons */}
                                        <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 2 }}>
                                            <Button
                                                size="small"
                                                onClick={() => {
                                                    setTtsRate(1);
                                                    setTtsPitch(1);
                                                    setTtsVolume(1);
                                                }}
                                                sx={{
                                                    color: '#ED6628',
                                                    border: '1px solid #ED6628',
                                                    '&:hover': {
                                                        backgroundColor: '#ED6628',
                                                        borderColor: '#ED6628',
                                                        color: '#fff',
                                                    },
                                                }}
                                            >
                                                Reset defaults
                                            </Button>
                                            <Button
                                                size="small"
                                                sx={{
                                                    color: '#ED6628',
                                                    border: '1px solid #ED6628',
                                                    '&:hover': {
                                                        backgroundColor: '#ED6628',
                                                        borderColor: '#ED6628',
                                                        color: '#fff',
                                                    },
                                                }}
                                                onClick={() => setTtsSettingsAnchor(null)}
                                            >
                                                Close
                                            </Button>
                                        </Box>
                                    </Box>
                                </Popover>

                            </div>

                            <Button
                                variant="outlined"
                                sx={{
                                    fontSize: {xs: '12px', sm: 'auto',},
                                    border: '1px solid #0097B2',
                                    color: '#0097B2',
                                    backgroundColor: '#fff',
                                    padding: {xs:'1px 8px',sm:'4px 12px', lg:'8px 20px'},
                                    borderRadius: '20px',
                                    opacity: (currentSegment === segments.length - 1 && !hasNextSession) ? 0.5 : 1,
                                    minWidth: {xs:'80px', sm:'100px', lg:'220px'},
                                }}
                                onClick={handleNextSegment}
                                disabled={currentSegment === segments.length - 1 && !hasNextSession}
                            >
                                {isLastSegmentWithNextSession ? 'View Next Session' : 'View Next Segment'}
                            </Button>
                        </Box>

                        {/* Launch Program Button */}
                        {/*<Box style={{*/}
                        {/*    display: 'flex',*/}
                        {/*    justifyContent: 'center',*/}
                        {/*    width: '100%',*/}
                        {/*    marginBottom: '20px'*/}
                        {/*}}>*/}

                        {/*    <Dialog open={dialogOpen} onClose={handleDialogClose}*/}
                        {/*            PaperProps={{*/}
                        {/*                sx: {*/}
                        {/*                    paddingBottom: '20px',*/}
                        {/*                    borderRadius: '15px',*/}
                        {/*                    overflow: {xs: 'scroll', sm: 'hidden'},*/}
                        {/*                }*/}
                        {/*            }}>*/}
                        {/*        <DialogContent>*/}
                        {/*            <Typography>Are you sure you want to launch this program?</Typography>*/}
                        {/*        </DialogContent>*/}
                        {/*        <DialogActions sx={{justifyContent: 'center'}}>*/}
                        {/*            <Button*/}
                        {/*                onClick={handleDialogClose}*/}
                        {/*                variant="outlined"*/}
                        {/*                style={{*/}
                        {/*                    color: '#FF5757',*/}
                        {/*                    borderColor: '#FF5757',*/}
                        {/*                    fontWeight: 600,*/}
                        {/*                    padding: '5px 35px',*/}
                        {/*                    borderRadius: '20px',*/}
                        {/*                }}*/}
                        {/*            >*/}
                        {/*                No*/}
                        {/*            </Button>*/}
                        {/*            <Button*/}
                        {/*                onClick={handleLaunchProgram}*/}
                        {/*                variant="contained"*/}
                        {/*                style={{*/}
                        {/*                    backgroundColor: '#FF5757',*/}
                        {/*                    color: '#fff',*/}
                        {/*                    fontWeight: 600,*/}
                        {/*                    padding: '5px 35px',*/}
                        {/*                    borderRadius: '20px',*/}
                        {/*                    boxShadow: '0 2px 4px rgba(0,0,0,0.1)',*/}
                        {/*                }}*/}
                        {/*            >*/}
                        {/*                Yes*/}
                        {/*            </Button>*/}
                        {/*        </DialogActions>*/}
                        {/*    </Dialog>*/}
                        {/*</Box>*/}
                    </Box>

                    {/* Right column slide navigation arrow buttons */}
                    {(currentSlide > 0 || showPrevSegmentButton) && (
                        <IconButton
                            onClick={handlePrevSlide}
                            disableFocusRipple
                            disableRipple
                            sx={{
                                "&:focus": {outline: "none"},
                                "&:focus-visible": {outline: "none"},
                            }}
                            style={{
                                paddingLeft: '0px',
                                paddingRight: '0px',
                                outline: 'none',
                                position: 'absolute',
                                top: '50%',
                                left: '-8px',
                                transform: 'translateY(-50%)',
                                transition: 'all 0.3s ease',
                                backgroundColor: '#ef6528',
                                borderTopRightRadius: '56px',
                                borderBottomRightRadius: '56px',
                                borderTopLeftRadius: 0,
                                borderBottomLeftRadius: 0,
                                cursor: 'pointer',
                                width: 56,
                                height: 112,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                color: '#fff',
                                boxShadow: '0 8px 18px rgba(239,101,40,0.25)'
                            }}
                        >
                            <ArrowLeft style={{fontSize: 120}}/>
                        </IconButton>
                    )}
                    {((currentSegmentSlides.length > 1 && currentSlide < currentSegmentSlides.length - 1) || showNextSegmentButton) && (
                        <IconButton onClick={handleNextSlide}
                                    disableFocusRipple
                                    disableRipple
                                    sx={{
                                        "&:focus": {outline: "none"},
                                        "&:focus-visible": {outline: "none"},
                                    }}
                                    style={{
                                        paddingRight: '0px',
                                        paddingLeft: '0px',
                                        outline: 'none',
                                        position: 'absolute',
                                        top: '50%',
                                        right: '-7px',
                                        transform: 'translateY(-50%)',
                                        transition: 'all 0.3s ease',
                                        backgroundColor: '#ef6528',
                                        borderTopLeftRadius: '56px',
                                        borderBottomLeftRadius: '56px',
                                        borderTopRightRadius: 0,
                                        borderBottomRightRadius: 0,
                                        cursor: 'pointer',
                                        width: 56,
                                        height: 112,
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        color: '#fff',
                                        boxShadow: '0 8px 18px rgba(239,101,40,0.25)'
                                    }}
                        >
                            <ArrowRight style={{fontSize: 120}}/>
                        </IconButton>
                    )}

                </Box>
            ) : (
                <Box className={classes.rightContainer}
                     style={{display: 'flex', alignItems: 'center', justifyContent: 'center'}}>
                    <Typography variant="h5" style={{color: '#777', textAlign: 'center'}}>
                        Select a session from the menu to view content
                    </Typography>
                </Box>
            )}

            {/* Assessment Visibility Modal */}
            <Dialog
                open={assessmentModalOpen}
                onClose={handleCloseAssessmentModal}
                PaperProps={{
                    sx: {
                        border: '1px solid #000',
                        borderRadius: '8px',
                        paddingBottom: '20px',
                        maxWidth: '500px',
                        overflow: 'hidden',
                    }
                }}
            >
                <DialogContent sx={{ padding: '40px 20px', textAlign: 'center' }}>
                    <Typography variant="body1" style={{ fontSize: '16px', lineHeight: '1.6', color: '#333', fontWeight: 500 }}>
                        {assessmentModalAction === 'hide' ? (
                            <>
                                Hiding the assessment will remove both the pre- and post-assessments for your students from all sessions in this entire module.
                                <br />
                                Are you sure you want to continue?
                            </>
                        ) : (
                            <>
                                When you unhide this assessment, both the pre- and post-assessments from all sessions in this module will become visible to your students again.
                                <br />
                                Are you sure you want to continue?
                            </>
                        )}
                    </Typography>
                </DialogContent>
                <DialogActions sx={{ justifyContent: 'center', gap: 2, padding: '0 30px 20px 20px', flexDirection: 'row' }}>
                    <Button
                        onClick={handleCloseAssessmentModal}
                        variant="contained"
                        sx={{
                            backgroundColor: '#ff6468',
                            color: '#fff',
                            fontWeight: 600,
                            padding: '6px 40px',
                            borderRadius: '20px',
                            textTransform: 'none',
                            '&:hover': {
                                backgroundColor: '#ff6468',
                                opacity: 0.9
                            }
                        }}
                    >
                        No
                    </Button>
                    <Button
                        onClick={handleConfirmAssessmentVisibility}
                        variant="contained"
                        sx={{
                            backgroundColor: '#0097b2',
                            color: '#fff',
                            fontWeight: 600,
                            padding: '6px 40px',
                            borderRadius: '20px',
                            textTransform: 'none',
                            '&:hover': {
                                backgroundColor: '#0097b2',
                                opacity: 0.9
                            }
                        }}
                    >
                        Yes
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default DQProProgPreview;
